import type { LibraryCommand, LibraryReport } from '../runner-libraries.ts';
import type { RunnerStorageView } from '../runner-storage.ts';
/**
 * The management interface's wire contract.
 *
 * Type-only, imported by BOTH the orchestrator (which produces these shapes) and the
 * dashboard bundle (which consumes them), so a field renamed on one side fails to compile
 * on the other. Nothing here may import a runtime value — the dashboard is bundled by Vite
 * and must not pull `better-sqlite3` into the browser.
 *
 * Timestamps are epoch milliseconds throughout, as everywhere else in Nightshift.
 */
import type { NightManagerHandoff } from '../night-manager/types.ts';
import type { Capability, Discipline, EngineFamily, ProjectPhase, ProjectProfile, WorkItemType } from '../capabilities/catalog.ts';

export type { Capability, ProjectPhase };
import type {
  AttemptStatus,
  DomainEventType,
  FailureType,
  RefiningActivity,
  TicketActivity,
  WaitingReasonType,
  WakeMode,
  DeploymentStatus,
  DeploymentHealthStatus,
} from '../db/schema.ts';
import type { Harness } from '../harnesses.ts';
import type { JobStage, Stage } from '../stages.ts';
import type { TicketState } from '../states.ts';
import type {
  FootprintView,
  SuggestionView,
  TicketSuggestions,
} from './ticket-suggestions.ts';
import type { Vendor } from '../vendors.ts';
import type { WorkCategory } from '../work-category.ts';
import type { ProjectConfiguration } from '../config/types.ts';

export type { AttemptStatus, FailureType, Harness };
export type { FootprintView, SuggestionView, TicketSuggestions };

export type BotRoleName = 'refine-bot' | 'impl-bot' | 'test-bot' | 'review-bot';

/** Why a queued job is not running. Operationally distinct; never flatten these. */
type QueueWaitReason =
  | 'slot' // a cap or a busy runner
  | 'backoff' // a retry after a crash/stall/timeout
  | 'deferred' // waiting on the vendor (rate limit / overload); attempt untouched
  | 'runner-offline'
  | 'no-eligible-runner'
  | 'dispatch-paused';

interface UiEngineIdentity {
  family: EngineFamily;
  name: string;
  version: string;
}

export interface UiProject {
  id: number;
  slug: string;
  name: string;
  key: string;
  defaultBranch: string;
  gitRepoPath: string;
  ticketCount: number;
  openCount: number;
  waitingCount: number;
  /** Named arrangement over capabilities — not a forked product. */
  profile: ProjectProfile;
  phase: ProjectPhase;
  engine: UiEngineIdentity;
  /** Enabled optional capabilities after profile arrangement + explicit overrides. */
  capabilities: Capability[];
  disciplines: Discipline[];
  workItemTypes: WorkItemType[];
}

/**
 * Lean project list for GET /api/projects (Create Ticket).
 * Snapshot still 500s on demolished jobs SQL; this is the dialog's project source.
 */
export interface ProjectListItem {
  slug: string;
  name: string;
  key: string;
  id: number;
  defaultBranch: string;
  gitRepoPath: string;
  gitRepoId: string | null;
  /** Null when deploy.job is empty. */
  deploy: ProjectDeploySummary | null;
}

interface ProjectDeploySummary {
  job: string;
  environment: string;
  healthUrl: string;
}

export interface ProjectList {
  projects: ProjectListItem[];
}

/**
 * Presentation state of one recorded dependency row (`ticket_dependencies`).
 *
 * Derived, never stored: the durable row only names a blocker key, and what that key
 * means today depends on the blocker ticket's place on the roster.
 *  - `blocked`     — the blocker exists and is unresolved
 *  - `satisfied`   — the blocker is Done (the row clears on the next release sweep)
 *  - `missing`     — the blocker key is not on the roster at all
 *  - `stale`       — the blocker exists, is unresolved, and has not moved in a long while
 *  - `unavailable` — the blocker was Cancelled, so waiting on it will never end
 */
export type DependencyChipState = 'blocked' | 'satisfied' | 'missing' | 'stale' | 'unavailable';

/**
 * One dependency edge rendered as a chip. On a ticket's `blockedBy` the key is the blocker;
 * on `blocking` the key is the dependent waiting on this ticket.
 */
export interface UiDependencyChip {
  key: string;
  state: DependencyChipState;
  /** When the dependency row was recorded. */
  since: number;
}

/** One confirmed dependency edge, from the point of view of the ticket being inspected. */
export interface TicketDependencyEdge {
  automatic?: boolean;
  reason?: string;
  ticketId: number;
  ticketKey: string;
  title: string;
  state: TicketState;
  chip: DependencyChipState;
  /** When the edge was recorded. */
  since: number;
  /**
   * False when the other ticket is in a different project. New edges cannot be created
   * across projects; an old one can still be here, and it must stay visible so it can be
   * removed rather than quietly ignored.
   */
  sameProject: boolean;
}

export interface TicketDependencies {
  /** Blockers: what this ticket depends on. */
  dependsOn: TicketDependencyEdge[];
  /** Dependents: what depends on this ticket. */
  blocks: TicketDependencyEdge[];
  /** Blockers that are not Done. What the Start gate refuses on. */
  unmetCount: number;
}

export interface UiTicket {
  id: number;
  projectId: number;
  projectSlug: string;
  projectKey: string;
  key: string;
  title: string;
  state: TicketState;
  cycleCap: number;
  mrIid: number | null;
  createdAt: number;
  updatedAt: number;
  /** When the ticket entered `state` — from ticket_state_history, not `updated_at`. */
  stateSince: number;
  /**
   * Where this ticket continues if it is started again — the state it stopped in, read from
   * its history. Null unless it is resting in Backlog or Awaiting Human with work behind it;
   * a ticket that has never been anywhere has nothing to resume, only somewhere to start.
   */
  resumeTarget: TicketState | null;
  /** Vendor owning the current stage, when a stage is in flight or was last run. */
  vendor: Vendor | null;
  role: BotRoleName | null;
  evidenceCount: number;
  spendUsd: number | null;
  runCount: number;
  /** Live stage run for this ticket, if one is running right now. */
  activeRunId: number | null;
  waitingOnHuman: boolean;
  /** One plain-language line for the human states: "refine-bot asked 2 questions". */
  handoverReason: string | null;
  /** Tracker labels, mirrored. Empty when never synced — not the same as "no type". */
  labels: string[];
  /** From `type:<id>` labels. Null when untyped; never guessed as feature. */
  workItemType: WorkItemType | null;
  /** From `discipline:<id>` labels. */
  disciplines: Discipline[];
  /** Derived from the refine template plus evidence/review; zeros when unrefined. */
  criteriaProgress: UiCriteriaProgress;
  /** Open dependency rows against this ticket, with presentation state derived. */
  blockedBy: UiDependencyChip[];
  /** Tickets waiting on this one, with the same derived state from their side of the edge. */
  blocking: UiDependencyChip[];
}

export interface UiAgent {
  runId: number;
  jobId: number;
  ticketId: number;
  ticketKey: string;
  ticketTitle: string;
  projectSlug: string;
  stage: JobStage;
  role: BotRoleName;
  vendor: Vendor;
  model: string;
  runnerName: string;
  startedAt: number;
  attempt: number;
  maxAttempts: number;
  /** Runner-enforced wall clock, ms. Honest: the health bar drains against this. */
  timeoutMs: number;
  /** Runner-enforced silence window, ms. */
  stallMs: number;
  /** Epoch ms of the last log chunk received, or null when nothing has streamed yet. */
  lastLogAt: number | null;
  logTail: string[];
  /** Spend on this ticket so far — completed runs only; there is no live token stream. */
  ticketSpendUsd: number | null;
  /**
   * Whether this agent can still actually be working, decided once by the server.
   *
   * `running` is doing its job. `silent` has streamed nothing for longer than is normal but is
   * still inside its wall clock. `orphaned` has outlived the wall clock the runner itself
   * enforces, so it cannot be alive: the runner died mid-job, or the orchestrator was down when
   * the reclaim sweep should have collected it.
   *
   * This is on the payload rather than derived per view because it was previously worked out
   * in one card component, and every other surface went on counting the row: a refine-bot
   * orphaned for 241 hours headlined the live-agents strip with a ticking clock, filled a slot
   * in the "1/2 refine" capacity summary, and was counted by the Ops AGENTS RUNNING tile. A
   * monitoring tool for autonomous agents has one non-negotiable — when it says something is
   * alive, that must be true — so the judgement is made once and every renderer obeys it.
   */
  liveness: AgentLiveness;
}

export type AgentLiveness = 'running' | 'silent' | 'orphaned';

export interface UiQueueItem {
  jobId: number;
  stage: JobStage;
  status: 'queued' | 'claimed' | 'running';
  ticketId: number;
  ticketKey: string;
  ticketTitle: string;
  projectSlug: string;
  targetRunner: string;
  attempt: number;
  maxAttempts: number;
  deferrals: number;
  notBefore: number;
  createdAt: number;
  error: string | null;
  reason: QueueWaitReason;
  /** Set when `reason` is `no-eligible-runner`: why this job cannot be claimed. */
  waitDetail: string | null;
}

interface UiRunnerJob {
  jobId: number;
  stage: JobStage;
  ticketKey: string;
  startedAt: number | null;
}

export interface UiRunner {
  name: string;
  platform: string;
  version: string | null;
  status: 'online' | 'offline';
  lastHeartbeatAt: number | null;
  tags: string[];
  current: UiRunnerJob[];
  runsToday: number;
  /**
   * Whether this runner can claim work: status is `online` and the heartbeat is fresh.
   * Tile, queue and sidebar all read this — never recompute it.
   */
  live: boolean;
  /** True when the heartbeat itself is older than the staleness window. */
  stale: boolean;
  /** Names the condition that failed when `live` is false. */
  offlineReason: string | null;
}

export type AlertKind =
  | 'question'
  | 'merge'
  | 'escalation'
  | 'bounce-cap'
  | 'failed-run'
  | 'blocked'
  | 'orphaned-job'
  | 'risky-gate'
  | 'missing-config'
  /**
   * Operator alerts: conditions about the factory itself rather than one ticket, which until
   * now only wrote marker files or said nothing. Evaluated by housekeeping into
   * `operator_alerts`, and cleared there the moment the condition clears.
   * See `core/src/ui/operator-alerts.ts`.
   */
  | 'harness-login'
  | 'runner-offline'
  | 'backup-failed'
  | 'offsite-backup-failed'
  /** A runner's library or system drive is low on free space. See `core/src/ui/disk-alerts.ts`. */
  | 'disk-low';

type AlertSeverity = 'critical' | 'high' | 'medium' | 'low';

type AlertAction = 'reply' | 'merge' | 'inspect' | 'open-setup' | 'open-machines' | 'open-ops';

export interface UiAlert {
  id: string;
  kind: AlertKind;
  severity: AlertSeverity;
  /** Who should act. The dashboard has one operator identity today. */
  owner: 'operator';
  ticketId: number;
  ticketKey: string;
  ticketTitle: string;
  projectSlug: string;
  state: TicketState;
  headline: string;
  since: number;
  mrIid: number | null;
  action: AlertAction;
  /** Named blockers when `kind` is `blocked`. */
  blockerKeys?: string[];
  /** Live run to open when `kind` is `orphaned-job`. */
  runId?: number;
  /**
   * True for a condition that has simply always been true, rather than a thing that started
   * waiting at a moment worth counting from.
   *
   * A missing adapter is not 47 minutes old; it has stood since the system was set up. Its
   * `since` is the orchestrator's own boot time, which is the best clock available and the
   * wrong one to publish — a restart reset it, so a permanent gap displayed as "2s" and read
   * as brand new. Standing items show what they are instead of a countdown.
   */
  standing?: boolean;
}

/**
 * How many things need a person, counted once.
 *
 * Four surfaces used to answer this question independently and gave four answers: the nav
 * badge said 12 (every item), the top-bar pill said 9 (high and critical only, excluding
 * low-severity config gaps and saying nothing about it), the board rail said 3, and Overview's
 * "Needs a person" listed a fourth subset. Three adjacent numbers for one idea, with no
 * explanation anywhere, is not a rounding difference — it teaches the operator that the
 * numbers on this product cannot be trusted.
 *
 * So the split is computed here and published. A surface may show `blocking` alone, but only
 * if it says so, and every surface counts from the same arithmetic.
 */
export interface UiAttentionCount {
  /** Everything in the inbox: the number the nav badge and the pill both show. */
  total: number;
  /** Items that hold up delivery — high and critical. */
  blocking: number;
  /** Standing configuration gaps: real, worth fixing, not blocking a ticket today. */
  config: number;
}

/** Snapshot `inbox` is `UiAlert[]` — there is no separate inbox page payload. */

/* ── universal search (P8.17) ──────────────────────────────────────────────── */

/**
 * The record families universal search ranges over. Each is an existing derived model —
 * search composes the hub builders and repositories; it stores nothing of its own.
 */
export const SEARCH_KINDS = [
  'ticket',
  'project',
  'run',
  'evidence',
] as const;

export type SearchKind = (typeof SEARCH_KINDS)[number];

export interface UiSearchResult {
  kind: SearchKind;
  /** Stable identifier within the kind (`ticket:12`, `run:4`, `ingest:12`). */
  id: string;
  title: string;
  detail: string | null;
  projectSlug: string | null;
  /** Stable link into an existing route (`/board?ticket=`, `/runs?ticket=`, …). */
  href: string;
  /** Set when the result opens the ticket inspector rather than navigating. */
  ticketKey?: string;
  /** Set when the inspector should open straight onto this run's transcript. */
  runId?: number;
  /** Recency tiebreak; epoch ms. */
  at: number;
  /** Match strength from the ranker; only useful relative to the other results. */
  score: number;
}

export interface UiSearchResults {
  generatedAt: number;
  query: string;
  /** The scope searched; null means every project. */
  projectSlug: string | null;
  /** True when more matches existed than the returned window holds. */
  truncated: boolean;
  items: UiSearchResult[];
}

type MilestoneKind = 'cycle' | 'module';

type MilestoneStatus = 'upcoming' | 'current' | 'completed' | 'undated';

interface UiMilestoneTicket {
  id: number;
  key: string;
  title: string;
  state: TicketState;
}

/**
 * One ingested tracker cycle (wave) or module, plus Nightshift's target-build SHA.
 * Progress is derived from linked tickets. The SHA is stored on the milestone; hrefs land on Board.
 */
/** A phase: a group of milestones worked together, with the phases it is planned after (order only). */
export interface UiPhase {
  id: number;
  name: string;
  orderAfter: number[];
  milestoneCount: number;
}

export interface UiMilestone {
  id: number;
  kind: MilestoneKind;
  status: MilestoneStatus;
  trackerId: string;
  name: string;
  detail: string | null;
  projectSlug: string;
  startAt: number | null;
  endAt: number | null;
  remainingMs: number | null;
  ticketCount: number;
  doneCount: number;
  openCount: number;
  targetSha: string | null;
  targetHref: string | null;
  tickets: UiMilestoneTicket[];
  href: string;
  /** Milestones this one directly comes after. Its tickets cannot start until those are finished. */
  after: { id: number; name: string }[];
  /** Milestones this one is placed after without waiting for them: layout and working order only. */
  orderAfter: { id: number; name: string }[];
  /** An AI-written paragraph on what the milestone is and holds; null until one has been generated. */
  summary: string | null;
  /** The phase this milestone belongs to, or null for none. */
  phase: string | null;
}

/**
 * Budgets screen. One lane per canonical metric that has
 * `performance_samples` rows and/or a Settings `performance_targets` row.
 * Deep-link ids are metric names (`fps_avg`), not the retired v0.12 catalog.
 *
 * This payload does not fail CI. Regression flags are visual. The gate is the performance-change profile.
 */
export const BUDGET_WINDOW_DAYS = 90;

type BudgetPointContext = 'nightly' | 'mr';

export type BudgetLaneStatus = 'ok' | 'regression' | 'empty' | 'no-baseline';

export interface UiBudgetPoint {
  t: number;
  value: number;
  machine: string;
  sha: string;
  probe: string | null;
  context: BudgetPointContext;
  pipelineId: number | null;
  /** Target miss, or MR worse than the most recent nightly for this metric+probe. */
  regression: boolean;
  /** MR with no nightly to compare. Not a regression on its own. */
  noBaseline: boolean;
}

export interface UiBudgetTargetLine {
  metric: string;
  comparison: PerformanceTargetComparison;
  value: number;
  unit: string | null;
}

export interface UiBudgetProbe {
  name: string;
  unit: string;
  nightly: UiBudgetPoint[];
  mr: UiBudgetPoint[];
  /** True when any point on this probe is a regression. */
  regression: boolean;
  /** True when this probe has MR points and no nightly. */
  noBaseline: boolean;
}

export interface UiBudgetLane {
  id: string;
  metric: string;
  label: string;
  unit: string;
  target: UiBudgetTargetLine | null;
  nightly: UiBudgetPoint[];
  mr: UiBudgetPoint[];
  probes: UiBudgetProbe[];
  /** True when any probe regresses. Does not pick a winning probe. */
  regression: boolean;
  noBaseline: boolean;
  status: BudgetLaneStatus;
  href: string;
}

/** Per-project (or all-projects) performance lanes over an explicit time window. */
export interface UiBudgets {
  generatedAt: number;
  projectSlug: string | null;
  projectId: number | null;
  windowDays: number;
  windowFrom: number;
  windowTo: number;
  lanes: UiBudgetLane[];
}

type AttemptResultType =
  | 'success'
  | 'failure'
  | 'cancelled'
  | 'interrupted'
  | 'superseded';

/** Pinned execution definition, never updated. Hash lives in the advanced panel. */
export interface UiAttemptDefinition {
  /** Friendly label: state / activity / prompt_ref. */
  label: string;
  contentHash: string;
}

/**
 * One `execution_attempts` row for the Runs ledger.
 *
 * A retry is its own row (`ordinal`). `requested_profile` is a machine name, not a model —
 * `machine` is the joined machines.name when present. Harness and model come from the
 * pinned definition's `tool_config.harness` and model list.
 */
export interface UiAttemptRun {
  id: number;
  /**
   * Which lane the row came from. Ids are unique per lane, not across them, so anything
   * keying or linking a row has to carry this too.
   *
   * A sidecar is not an attempt: it takes no worker slot, holds up no ticket, and has no
   * execution definition. Listing the two together answers "what ran and what did it cost";
   * conflating them would answer "how is this ticket going" wrongly.
   */
  lane: 'attempt' | 'sidecar';
  /** Present only on a sidecar row. */
  sidecarKind?: 'footprint' | 'dependency-scan' | 'brief-cleanup' | 'text-task' | 'creation-draft';
  ordinal: number;
  ticketId: number | null;
  ticketKey: string | null;
  projectSlug: string;
  state: TicketState | null;
  activity: TicketActivity | null;
  status: AttemptStatus;
  resultType: AttemptResultType | null;
  failureType: FailureType | null;
  /**
   * What the runner said, when it said anything. `failureType` is a routing bucket, so on its
   * own it cannot tell an operator whether to change a route, a prompt or a machine.
   */
  failureDetail?: string;
  harness: Harness | null;
  model: string | null;
  reviewerName: string | null;
  machine: string | null;
  startedAt: number | null;
  finishedAt: number | null;
  durationMs: number | null;
  /**
   * The wall clock the runner reported when it started this attempt; null when it reported
   * none. Optional because sidecar rows and older payloads carry no budget at all.
   */
  timeoutMs?: number | null;
  tokensIn: number;
  tokensOut: number;
  costUsd: number | null;
  definition: UiAttemptDefinition | null;
  hasTranscript: boolean;
  logRef: string | null;
}

export interface UiSupply {
  stage: Stage;
  inFlight: number;
  cap: number;
  /** `central` when Settings sets the cap; `default` when the built-in cap applies. */
  capSource: 'default' | 'central';
}

export interface UiCostBucket {
  label: string;
  runs: number;
  tokens: number;
  usd: number | null;
}

export interface UiCosts {
  todayUsd: number;
  totalUsd: number;
  runsToday: number;
  byModel: UiCostBucket[];
  byStage: UiCostBucket[];
  byTicket: UiCostBucket[];
  byProject: UiCostBucket[];
  /** Runs with no usage envelope: the passthrough plus anything that died before reporting. */
  unknownRuns: number;
}

export interface UiTickerItem {
  id: string;
  at: number;
  /** Plain language, past tense: "review-bot raised 4 blockers on GRID-6". */
  text: string;
  tone: 'neutral' | 'good' | 'warn' | 'bad' | 'human';
  ticketKey: string | null;
}

interface UiHealth {
  /** Release identity (`0.12.1`, plus `+<sha>` when a git object name is in env). */
  version: string;
  uptimeSeconds: number;
  db: 'ok' | 'error';
  pipelineActive: boolean;
  serverTime: number;
}

export interface UiSnapshot {
  revision: number;
  now: number;
  health: UiHealth;
  projects: UiProject[];
  /** Null means "all projects" scope. */
  projectSlug: string | null;
  tickets: UiTicket[];
  agents: UiAgent[];
  queue: UiQueueItem[];
  runners: UiRunner[];
  /**
   * Actionable handovers surfaced in the pill, title, favicon and chime.
   * A filter of `inbox` (high/critical), not a second model.
   */
  alerts: UiAlert[];
  /** Every derived condition that still needs a human. Clears only when the condition clears. */
  inbox: UiAlert[];
  /** The one count of what needs a person, so no two surfaces can disagree about it. */
  attention: UiAttentionCount;
  supply: UiSupply[];
  costs: UiCosts;
  recentRuns: UiAttemptRun[];
  recentFailures: UiAttemptRun[];
  ticker: UiTickerItem[];
  dispatchPaused: boolean;
  stateCounts: Record<TicketState, number>;
}

/* ── ticket detail ─────────────────────────────────────────────────────────── */

interface UiEvidence {
  id: number;
  cycle: number;
  filename: string;
  /** Two-tier caption: headline, blank line, then the measured proof. */
  headline: string;
  proof: string;
  bytes: number;
  createdAt: number;
  url: string;
}

export type CriterionStatus = 'unproven' | 'demonstrated' | 'failed' | 'disputed';

type TicketSpecSource = 'tracker' | 'refine-result' | 'none';

export interface UiCriteriaProgress {
  total: number;
  demonstrated: number;
  failed: number;
  disputed: number;
  unproven: number;
}

export interface UiCriterionEvidence {
  id: number;
  cycle: number;
  headline: string;
}

export interface UiCriterionFinding {
  cite: string;
  blocking: boolean;
  why: string | null;
  axis: string | null;
}

export interface UiCriterionDispute {
  criterion: string;
  problem: string;
}

export interface UiCriterion {
  /** 1-based acceptance-criterion number, matching "AC 2" cites. */
  index: number;
  text: string;
  status: CriterionStatus;
  evidence: UiCriterionEvidence[];
  findings: UiCriterionFinding[];
  dispute: UiCriterionDispute | null;
}

export interface UiTicketSection {
  id: string;
  heading: string;
  body: string;
}

/** Read-only tracker child; Nightshift does not own subtask state. */
export interface UiSubIssue {
  id: string;
  key: string;
  title: string;
  state: TicketState;
  url: string;
}

/**
 * First-class refine template plus evidence-derived criterion status.
 *
 * Parsed from the live tracker description when the drawer fetches it, otherwise from the
 * latest refine result already on disk. Not a second ticket.
 */
export interface UiTicketSpec {
  source: TicketSpecSource;
  /** Tracker description and last refine result disagree on criteria or template sections. */
  stale: boolean;
  sections: UiTicketSection[];
  criteria: UiCriterion[];
  progress: UiCriteriaProgress;
  subIssues: UiSubIssue[];
  subIssuesError: string | null;
}

/* ── evidence gallery ──────────────────────────────────────────────── */

/** One screenshot, with enough of its ticket attached to stand on its own in a grid. */
interface UiEvidenceItem extends UiEvidence {
  ticketId: number;
  ticketKey: string;
  ticketTitle: string;
  projectSlug: string;
  state: TicketState;
}

export interface UiEvidencePage {
  items: UiEvidenceItem[];
  total: number;
  offset: number;
  limit: number;
  /** Total bytes of every screenshot matching the filter, page window ignored. */
  totalBytes: number;
}

/* ── runs (paged) ─────────────────────────────────────────────────── */

/**
 * A page of execution attempts.
 *
 * The snapshot carries only the newest sixty, because it is refetched on every change and
 * paying for the whole run history on each one would be absurd. The Runs screen wants the
 * whole ledger, filtered, so it asks for it separately and pages through it.
 */
export interface UiRunsPage {
  runs: UiAttemptRun[];
  /** Rows matching the filter, ignoring the page window. */
  total: number;
  offset: number;
  limit: number;
}

/* ── config ────────────────────────────────────────────────────────────────── */

/** One coding-agent vendor and whether the operator will let it run. */
export interface UiVendorSwitch {
  vendor: string;
  enabled: boolean;
  /** Why it was switched off, when the operator said. Empty when enabled or unexplained. */
  reason: string;
  /** True when this is the only vendor left on: the switch refuses to turn it off. */
  lastOneStanding: boolean;
  /** Curated models this harness accepts, with operator-controlled selector availability. */
  models: UiAgentModel[];
}

interface UiAgentModel {
  id: string;
  label: string;
  harness: Harness;
  harnessLabel: string;
  enabled: boolean;
  family?: string;
  cliModel?: string;
  reasoningEffort?: string;
  supportsReasoning?: boolean;
}

export interface UiConfigResponse {
  dispatchPaused: boolean;
  vendors: UiVendorSwitch[];
  /**
   * The project scope actually applied, or null for the all-projects view.
   *
   * Settings is a production control surface, so it has to be able to prove what it is
   * showing. The server reports the scope it applied rather than trusting the caller to
   * remember what it asked for, and an unrecognised slug reports null with no project health
   * at all — never a silent widening to everything.
   */
  scope: string | null;
  /** True when a project scope was requested and no project owns that slug. */
  unknownProject: boolean;
  /** Project configuration issues, suitable for the persistent Settings indicator. */
  configHealth: UiConfigIssue[];
}

/**
 * `GET` and `PUT /api/projects/:id/configuration`: a project's one configuration document.
 *
 * `revision` is what a save must name as `expectedRevision`; a stale one is refused with 409 so
 * two editors cannot silently overwrite each other. `status` is `ready` unless the one-time
 * upgrade still owes this project its import, in which case `configuration` shows the built-in
 * defaults, `issues` says why, and the project does not dispatch until an administrator saves.
 */
export interface UiProjectConfiguration {
  ok: true;
  project: string;
  configuration: ProjectConfiguration;
  /** The documented built-in defaults, so the editor can say what an empty field runs. */
  defaults: ProjectConfiguration;
  revision: number;
  schemaVersion: number;
  status: 'ready' | 'import-pending' | 'import-failed';
  issues: string[];
  updatedAt: number | null;
  updatedBy: string | null;
  /** False for members: they see the same document and cannot save it. */
  canEdit: boolean;
  /** Non-blocking notes from the last save (a model the catalogue does not list, for example). */
  warnings: string[];
}

export interface UiConfigIssue {
  project: string;
  severity: 'blocking' | 'warning';
  message: string;
  path?: string;
}

/* ── live events ───────────────────────────────────────────────────────────── */

/**
 * SSE payloads.
 *
 * Two tiers on purpose. `store.changed` is a *hint*: something in the durable store moved,
 * refetch the snapshot (debounced). It is emitted both from the write paths — so the
 * latency is a few milliseconds, not a poll interval — and from a cheap digest watcher, so
 * a change made outside the HTTP API (the reclaim sweep, a CLI, the runner) can never leave
 * the screen stale. The remaining kinds carry data too hot or too chatty to round-trip:
 * log chunks, elapsed-time anchors, and the alert ping.
 */
export type UiEvent =
  | { kind: 'hello'; revision: number; serverTime: number }
  | { kind: 'store.changed'; revision: number; reason: string; at: number }
  | { kind: 'log.chunk'; runId: number; jobId: number; lines: string[]; at: number }
  | { kind: 'agent.started'; agent: UiAgent }
  | {
      kind: 'agent.finished';
      runId: number;
      ticketKey: string;
      stage: JobStage;
      costUsd: number | null;
      durationMs: number;
      ok: boolean;
    }
  | {
      kind: 'ticket.moved';
      ticketKey: string;
      from: TicketState | null;
      to: TicketState;
      bounce: boolean;
      at: number;
    }
  | { kind: 'alert.raised'; alert: UiAlert }
  | { kind: 'alert.cleared'; id: string }
  | { kind: 'ticker'; item: UiTickerItem }
  | { kind: 'ping'; at: number };

export interface UiActionResult {
  ok: boolean;
  message: string;
  /** Present on recovery commands: what changed and what happens next, in words. */
  receipt?: { command: string; ticketKey: string; changed: string[]; next: string; message: string };
  /** Present when the action changed a ticket state. */
  state?: TicketState;
  /** Present when the action created or moved a ticket. */
  ticketKey?: string;
  /** Config paths the setup wizard actually wrote. */
  applied?: string[];
  /** Config paths the wizard did not write, with why. */
  skipped?: { path: string; reason: string }[];
  /** Present for dependency creation; `created` is false for an idempotent concurrent retry. */
  dependency?: {
    id: number;
    prerequisiteId: number;
    dependentId: number;
    /** The chip status the graph would derive for this edge, so the canvas can draw it before it reloads. */
    status: DependencyChipState;
    createdAt: number;
    created: boolean;
  };
  /**
   * Present when recording a dependency found the ticket already moving.
   *
   * Mirrors `HoldOffer` in the lifecycle. It rides the success rather than an error because
   * the link was recorded either way; whether to park the ticket behind it is a separate
   * decision, and only the person looking at the run can weigh a rebase against a hold.
   */
  hold?: UiHoldOffer;
  /**
   * Present when a dependency was not recorded because the milestone order already makes the
   * ticket wait for the blocker. The sentence names the milestones.
   */
  impliedByMilestoneOrder?: string;
}

export interface UiHoldOffer {
  blockerTicketId: number;
  blockerKey: string;
  blockerState: TicketState;
  state: TicketState;
  attemptId?: number;
}

/* ── board read-model ─────────────────────────────────────────────────────── */

/**
 * Primary columns, left to right. Waiting is a side column (reason rows);
 * Cancelled is a collapsed rail. Neither belongs here.
 */
export const BOARD_PRIMARY_COLUMNS = [
  'Backlog',
  'Refining',
  'Implementing',
  'GitLab CI',
  'AI Review',
  'Human Review',
  'Merging',
  'Done',
] as const;

export type BoardPrimaryColumn = (typeof BOARD_PRIMARY_COLUMNS)[number];

/**
 * Waiting reason rows always rendered, even at count 0. Keys are
 * `waiting_requests.reason_type`. `infrastructure` and `merge_conflict_decision`
 * are appended only when at least one open request uses them. An unknown
 * reason_type is not a bucket — the query refuses rather than inventing one.
 */
export const BOARD_WAITING_REASON_ORDER = [
  'dependency',
  'plan_approval',
  'obsolete_proposal',
  'human_clarification',
  'child_tickets',
  'manual_hold',
  'attempt_limit',
] as const satisfies readonly WaitingReasonType[];

export const BOARD_WAITING_REASON_LABELS: Record<WaitingReasonType, string> = {
  // "Queued", not "Dependency": the row answers what is happening to the ticket, and what is
  // happening is that it is waiting its turn and will start itself. The edge that put it there
  // is a fact about the work, which the card's own chips carry.
  dependency: 'Queued',
  shared_edits: 'Shared edits',
  dependency_scan: 'Dependency scan',
  plan_approval: 'Plan Approval',
  obsolete_proposal: 'Proposed Cancellation',
  human_clarification: 'Clarification',
  child_tickets: 'Child Tickets',
  infrastructure: 'Infrastructure',
  configuration: 'Configuration',
  manual_hold: 'Manual Hold',
  attempt_limit: 'Attempt Limit',
  merge_conflict_decision: 'Merge Conflict Decision',
  rate_limit: 'Rate Limit',
};

/** Same four families as `REPAIR_FAMILIES` in repair-budgets.ts; duplicated so this file stays sqlite-free. */
type BoardRepairFamily = 'ci-repair' | 'ai-review-repair' | 'research-loop' | 'merge-conflict';

export interface BoardRepairCount {
  family: BoardRepairFamily;
  used: number;
  max: number;
}

export interface TicketDeployment {
  id: number;
  environment: string;
  sha: string;
  jobName: string;
  status: DeploymentStatus;
  pipelineId: number | null;
  jobId: number | null;
  jobUrl: string | null;
  healthUrl: string | null;
  healthStatus: DeploymentHealthStatus;
  healthDetail: string | null;
  /** Release identity returned by this deployment's successful health check. */
  releaseVersion: string | null;
  rollbackSha: string | null;
  createdAt: number;
  updatedAt: number;
  finishedAt: number | null;
}

/**
 * One card on the Dark Factory board. `UiTicket` cannot express activity chips,
 * typed waiting reasons, or n/5 repair families without lying about v0.12 fields.
 *
 * Elapsed is `now - tickets.updated_at` (last ticket-row change), not `created_at`
 * and not a dedicated state-entered clock (that table is gone).
 *
 * `machine`, `harness`, `model`, `mr`, and `executionSubstate` are null when there is no
 * attempt to read them from; this query does not invent them.
 */
export interface BoardCiStatus {
  sha: string;
  pipelineId: number;
  url: string;
  status: string;
  observedAt: number;
  jobs: BoardCiJob[];
  error: string | null;
}

export interface BoardCiJob {
  id: number;
  name: string;
  stage: string;
  status: string;
  url: string | null;
  /** The job's uploaded archive, when GitLab lists one. Older observations carry no field. */
  archive?: { filename: string; sizeBytes: number; expiresAt: number | null } | null;
}

/**
 * One link the card's artifacts popover offers.
 *
 * `url` is null when the declaration named a job this pipeline ran but which produced no
 * upload -- usually because it failed -- and the row says so instead of hiding the absence.
 */
export interface BoardArtifactLink {
  label: string;
  job: string;
  jobId: number;
  jobStatus: string;
  /** The file inside the archive, or the archive itself. */
  fileName: string | null;
  url: string | null;
  browseUrl: string | null;
  /** Archive size; unknown for a file inside one. */
  sizeBytes: number | null;
  expiresAt: number | null;
}

/**
 * What the pipeline for the card's branch head uploaded, for the artifacts popover.
 *
 * `key` is what the project's named artefacts singled out; `more`
 * is every other job archive. A card with nothing in either has no popover.
 */
export interface BoardArtifacts {
  pipelineId: number;
  pipelineUrl: string;
  sha: string;
  observedAt: number;
  key: BoardArtifactLink[];
  more: BoardArtifactLink[];
}

export interface BoardShippingStatus {
  status: 'shipped' | 'not-shipped' | 'unknown';
  mergeSha?: string;
  environment: string;
  sha: string | null;
  version: string | null;
  observedAt: number;
  detail: string;
}

export interface BoardCard {
  displayTitle?: string | null;
  displaySummary?: string | null;
  activeSince?: number | null;
  ci?: BoardCiStatus | null;
  shipping?: BoardShippingStatus | null;
  /** Downloads from the last observed pipeline, in any state; null when nothing was uploaded. */
  artifacts?: BoardArtifacts | null;
  id: number;
  ticketKey: string;
  title: string;
  /**
   * The contract revision (`tickets.version`), which moves only when the problem or the
   * acceptance criteria change. A reader showing those -- the dependency preview -- refetches
   * when this moves rather than on every board update. Optional for payloads older than 0.16.2.
   */
  version?: number;
  /** Ticket problem statement (Markdown). The card shows a plain-text excerpt; the drawer renders it. */
  description: string;
  state: TicketState;
  activity: TicketActivity | null;
  /** Milliseconds since `tickets.updated_at`. */
  elapsedMs: number;
  elapsedFrom: 'updated_at';
  repairs: BoardRepairCount[];
  waitingReason: WaitingReasonType | null;
  waitingReasonText: string | null;
  /**
   * The open wait will clear without a person: `wake_mode` is `automatic`.
   *
   * The difference the board draws in colour. A ticket queued behind a blocker and a ticket
   * held for a plan approval are both Waiting, and only one of them is anybody's problem;
   * painting both in the attention amber is what taught an operator to read past the rail.
   */
  waitingSelfWaking: boolean;
  machine: string | null;
  harness: string | null;
  model: string | null;
  mr: { iid: number; url: string } | null;
  executionSubstate: string | null;
  latestDeploy: TicketDeployment | null;
  /** True when a merge SHA exists so admin Deploy can play a job. Historic Done is false. */
  deployable: boolean;
  /** Execution attempts recorded for this ticket, every state, every outcome. */
  attemptCount: number;
  /** Attempts that ended `Failed`. A rising number on a card is the cheapest "look at me". */
  failedAttemptCount: number;
  /** Summed `cost_usd` over attempts; null when nothing has been priced. */
  costUsd: number | null;
  /**
   * Why nothing is happening, when the housekeeping detector has said so.
   *
   * Set from the newest `TicketStalled` with no `TicketRecovered` after it. The card wears a
   * chip; the sentence itself is the drawer's job. Null is the normal case.
   */
  stalled: string | null;
  /**
   * What the landing loop last decided, for a ticket in Merging.
   *
   * The loop already computes exactly this sentence every pass. Without it the Merging column
   * reads "N landing" and nothing else, which is the state MONO-4 was in for ten minutes while
   * it live-locked.
   */
  landing: { outcome: string; detail: string } | null;
  /**
   * Confirmed blockers that are not Done, and the count of tickets waiting on this one.
   *
   * Satisfied blockers are deliberately absent: a card that says "depends on GRID-12" when
   * GRID-12 landed last week is noise, and the drawer still lists it.
   */
  dependsOn: BoardDependencyChip[];
  blocksCount: number;
  /**
   * Undecided high-band proposals touching this ticket, either direction.
   *
   * A count, not a list: the chip's job is to say a decision is waiting, and the drawer is
   * where the evidence lives. Medium-band proposals are deliberately excluded, because a chip
   * that appears for a weak guess trains people to ignore the chip.
   */
  suggestedCount: number;
  /**
   * Started within the last hour with nothing having compared it against its siblings.
   *
   * An empty suggestion list means "nothing found" and "nobody looked" alike. This says which.
   */
  notScanned: boolean;
}

/** A card-sized dependency: enough to name the blocker and colour the chip. */
export interface BoardDependencyChip {
  ticketKey: string;
  title: string;
  state: TicketState;
  chip: DependencyChipState;
}

export interface BoardActivityGroup {
  key: RefiningActivity;
  label: string;
  cards: BoardCard[];
  count: number;
}

export interface BoardColumn {
  key: BoardPrimaryColumn;
  cards: BoardCard[];
  count: number;
  /** Present only on Refining: Standard / Research / Planning. */
  groups?: BoardActivityGroup[];
}

export interface BoardWaitingGroup {
  key: WaitingReasonType;
  label: string;
  cards: BoardCard[];
  count: number;
}

interface BoardSummary {
  /**
   * Non-terminal, non-waiting tickets in this filter (includes Backlog).
   * Waiting has its own strip number (`waitingNeedsAttention`).
   */
  inFlight: number;
  /** Open `waiting_requests` in this filter with `wake_mode = 'human'`. */
  waitingNeedsAttention: number;
  cancelled: number;
  activeAgents: number;
  ciPipelines: number;
}

export interface BoardProjection {
  projectId: number;
  milestoneId: number | null;
  generatedAt: number;
  columns: BoardColumn[];
  waiting: { groups: BoardWaitingGroup[]; count: number };
  cancelled: { cards: BoardCard[]; count: number };
  summary: BoardSummary;
  deployConfigured: boolean;
}

/* ── tickets index ────────────────────────────────────────────────────────── */

/**
 * One row on the Tickets index. Not a board card: this list is uncapped and
 * includes Waiting and Cancelled, which the board only shows as rail populations.
 *
 * `tags` is empty until a labels column exists — do not invent domain tags.
 * Elapsed is `now - tickets.updated_at`, same clock as the board.
 */
export interface TicketListRow {
  id: number;
  ticketKey: string;
  title: string;
  state: TicketState;
  activity: TicketActivity | null;
  waitingReason: WaitingReasonType | null;
  waitingReasonText: string | null;
  milestoneId: number | null;
  milestoneName: string | null;
  tags: string[];
  updatedAt: number;
  elapsedMs: number;
  elapsedFrom: 'updated_at';
  repairs: BoardRepairCount[];
}

/**
 * Flat ticket index for GET /api/tickets.
 *
 * `projectId` 0 means the requested slug was missing or unknown (empty items,
 * not an error — same as GET /api/board). `projectId` null means every project.
 */
export interface TicketList {
  projectId: number | null;
  milestoneId: number | null;
  generatedAt: number;
  items: TicketListRow[];
}

/* ── machines ─────────────────────────────────────────────────────────────── */

export interface MachineAttemptView {
  id: number;
  ticketKey: string;
  status: 'Queued' | 'Starting' | 'Running';
  state: TicketState;
  activity: TicketActivity | null;
  startedAt: number | null;
  /** Recorded budget, or null for a row an older runner started without reporting one. */
  timeoutMs: number | null;
}

/** Attempt/spend rollups on GET /api/machines look back this many UTC days. */
export const MACHINE_STATS_WINDOW_DAYS = 7;

/**
 * Finished-attempt and spend totals for one machine over `windowDays` UTC days.
 *
 * `spendUsd` is null when nothing is priced — never $0 for an empty or unknown
 * ledger. Token totals come from the attempt columns, not a second sum of
 * `cost_entries`.
 */
export interface MachineWindowStats {
  windowDays: number;
  since: number;
  finished: number;
  succeeded: number;
  failed: number;
  interrupted: number;
  cancelled: number;
  tokensIn: number;
  tokensOut: number;
  meanDurationMs: number | null;
  spendUsd: number | null;
  spendEntries: number;
  unknownSpendEntries: number;
  lastFinishedAt: number | null;
  lastTicketKey: string | null;
}

/**
 * One provider account's plan capacity on a machine, as its runner last reported it.
 *
 * `account` is a one-way digest, never an email address. `fresh` is the scheduler's own
 * judgement (`CAPACITY_FRESHNESS_MS`), so the card and the routing agree on whether the
 * figure still counts. `lowestRemainingPercent` is the tightest *plan-wide* window as of
 * `generatedAt`: what the plan has left for any model. A per-model window never sets it, so a
 * spent Fable week cannot be read as a spent account -- read `windows[]` for the per-model
 * picture. See docs/PROVIDER-CAPACITY.md.
 */
export interface MachineCapacityView {
  provider: 'codex' | 'claude';
  account: string;
  /** Plan token as reported (`max-20x`, `pro`, `plus`, ...). See `core/src/capacity/plans.ts`. */
  plan: string | null;
  /** The plan's display words (`Max 20x`, `Pro`, `Plus`), or null when the plan is unknown. */
  planLabel: string | null;
  status: 'available' | 'throttled' | 'exhausted' | 'unavailable' | 'error';
  confidence: 'high' | 'medium' | 'low' | 'none';
  /** Tightest plan-wide window, 0..100, or null when no plan-wide window carries a figure. */
  lowestRemainingPercent: number | null;
  windows: Array<{
    name: string;
    kind: 'session' | 'daily' | 'weekly' | 'monthly' | 'model' | 'credits' | 'other';
    model: string | null;
    usedPercent: number | null;
    remainingPercent: number | null;
    /** ISO-8601 UTC, or null. */
    resetsAt: string | null;
  }>;
  error: string | null;
  /** Epoch ms on the runner's clock. */
  collectedAt: number;
  /** Epoch ms on the orchestrator's clock. */
  receivedAt: number;
  fresh: boolean;
}

/**
 * One registered machine for GET /api/machines.
 *
 * `health` is the stored column. `live` is health=`online` AND a fresh heartbeat
 * (`HEARTBEAT_STALE_MS`). `token_hash` is never serialised here.
 */
/** A project a scoped machine serves, as the Machines card names it. */
interface MachineScopeProject {
  id: number;
  slug: string;
  name: string;
}

export interface MachineLibraryProject {
  key: string;
  name: string;
  libraryIds: string[];
  mainDirectories: string[];
  sidecarDirectories: string[];
  worktreeDirectories: string[];
  /** Old runners cannot report the directory roles yet. */
  unclassifiedDirectories: string[];
}

export interface MachineListRow {
  libraries?: { report: LibraryReport | null; command: LibraryCommand | null; projects?: MachineLibraryProject[] };
  /** Kept ticket worktrees and their clean-up. Admin view only; null until the runner reports. */
  storage?: RunnerStorageView | null;
  attachedDevices?: { id: string; state: string; observedAt: number }[];
  deviceLeases?: { attemptId: number; ticketKey: string; deviceId: string; acquiredAt: number }[];
  id: number;
  name: string;
  os: string;
  installedHarnesses: string[];
  /**
   * Login health per installed harness, from the runner's status-command probe and the
   * fleet-wide mark an AuthenticationFailure leaves. `failed` takes the harness out of route
   * eligibility on this machine. Absent entries mean nothing has been reported yet.
   */
  harnessAuth: MachineHarnessAuthView[];
  maxWorkers: number;
  /**
   * Sidecar slots, counted separately from worker slots.
   *
   * A worker slot throttles how fast tickets complete; a sidecar slot bounds how much cheap
   * background work one host runs at once. Adding them together would misreport both, and a
   * host reporting zero here is one whose runner predates sidecars.
   */
  maxSidecars: number;
  /** What an operator asked the runner's sidecar lane to become. */
  desiredSidecars: number | null;
  /** What an operator asked for, when that differs from what the runner reports running. */
  desiredWorkers: number | null;
  /** True between pressing Restart and the runner picking the request up on a heartbeat. */
  restartPending: boolean;
  /**
   * True while an operator has paused the machine: registered, heartbeating, but offered no
   * new attempts or sidecar jobs until resumed. What it was already running finishes.
   */
  paused: boolean;
  pausedAt: number | null;
  /**
   * The projects an operator keeps this machine for, or null when it serves every project.
   * Only projects the caller can see are named; `hiddenCount` counts the rest, so a restricted
   * caller still learns that the machine is scoped.
   */
  projectScope: { projects: MachineScopeProject[]; hiddenCount: number } | null;
  health: 'online' | 'offline' | 'unhealthy' | 'unknown';
  lastSeen: number | null;
  version: string | null;
  /**
   * How `version` compares with the orchestrator serving this list. `outdated` runners are
   * refused work until they update; the others may work. See `runnerVersionStatus`.
   */
  versionStatus: 'current' | 'drift' | 'patch' | 'outdated' | 'unknown';
  /** The orchestrator's own version, so the UI can say what "outdated" is measured against. */
  expectedVersion: string;
  registeredAt: number;
  live: boolean;
  stale: boolean;
  offlineReason: string | null;
  currentAttempts: MachineAttemptView[];
  /** Sidecar jobs this machine is running now. Never mixed into `currentAttempts`. */
  currentSidecars: MachineSidecarView[];
  /**
   * Plan capacity per provider account, from the runner's opt-in telemetry. Empty for a
   * machine whose runner does not collect it. Advisory: routing still requires liveness,
   * the harness and a free worker slot before it looks at this.
   */
  capacity: MachineCapacityView[];
  /**
   * The machine whose readings `capacity` shows, when this machine takes another's because its
   * CLIs spend through that machine's account proxy; null when the readings are its own.
   */
  capacitySource: string | null;
  /** The operator's floors for this machine. Empty means no floor. */
  capacityPolicy: MachineCapacityPolicyView;
  /** When every path fell under its floor, or null. See docs/PROVIDER-CAPACITY.md. */
  capacityHeldAt: number | null;
  /** Each installed harness and whether the floors leave it open. */
  capacityPaths: MachineCapacityPathView[];
  /**
   * What the machine is, from the runner's latest report: processor, memory, graphics,
   * fixed drives, displays. Null until a runner that collects them (0.18.9+) heartbeats.
   */
  hardware: MachineHardwareView | null;
  stats: MachineWindowStats;
}

export interface MachineHardwareView {
  runtimeReceipts?: Array<{ sourceHead: string; executableSha256: string; sessionId: string; topologySha256: string; collectedAt: number; expiresAt: number }>;
  cpu: { model: string; cores: number } | null;
  memory: { totalBytes: number; freeBytes: number } | null;
  gpus: Array<{ name: string; memoryBytes: number | null }>;
  drives: Array<{ mount: string; totalBytes: number; freeBytes: number; filesystem: string | null }>;
  displays: Array<{ width: number; height: number; hertz: number | null }>;
  /** Epoch ms on the orchestrator's clock when the report was stored. */
  receivedAt: number;
  /** Processor architecture, when the runner reported it. */
  arch: string | null;
  /** Toolchains probed under the runner account; empty from runners before 0.19.9. */
  toolchains: Array<{ id: string; status: 'detected' | 'missing' | 'error'; versions: string[]; detail: string | null }>;
  /** Whether the runner's own session can render, when it reported it. */
  session: { graphics: 'available' | 'unavailable' | 'unknown'; detail: string | null } | null;
}

/** One machine judged against one role's environment contract for a project. */
interface ProjectCompatibilityRow {
  machineId: number;
  machine: string;
  host: string;
  live: boolean;
  compatible: boolean;
  reasons: Array<{ code: string; detail: string; source: 'project' | 'repository' | 'central' }>;
  matched: Array<{ id: string; versions: string[] }>;
}

export interface ProjectCompatibilityRole {
  role: 'refiner' | 'implementer' | 'reviewer';
  operations: string[];
  hosts: string[];
  tools: string[];
  graphicsSession: boolean;
  /** True when the role must also be able to post synthetic input into that session. */
  inputSession: boolean;
  /** True when the contract can refuse a machine at all. */
  constrains: boolean;
  machines: ProjectCompatibilityRow[];
}

/** `GET /api/projects/:id/compatibility`: the environment matrix Settings shows. */
export interface ProjectCompatibilityView {
  declared: boolean;
  /** `project` when the project's configuration declares an environment. */
  source: 'project' | null;
  shell: string;
  workingDir: string;
  commands: { build: string; test: string };
  roles: ProjectCompatibilityRole[];
}

export interface MachineCapacityPolicyView {
  /**
   * Floors keyed by `<provider>:<window name>` (`claude:five_hour`, `claude:seven_day_fable`,
   * `codex:primary`), each 1..100: the remaining percentage that window must keep. A plan-wide
   * window under its floor closes the provider on this machine; a model-scoped one closes
   * only that model. A window with no key has no floor.
   */
  floors: Record<string, number>;
  /** Lift a hold on its own when a path recovers. */
  autoResume: boolean;
}

/** One harness's login on one machine, for the Agents view. Never names an account. */
export interface MachineHarnessAuthView {
  harness: string;
  state: 'ok' | 'failed' | 'unknown';
  /** Where the state came from: the runner's probe, or an attempt that failed to authenticate. */
  source: 'probe' | 'attempt';
  /** Epoch ms the state was established. */
  checkedAt: number;
  /** A fixed classification such as `claude auth status: not logged in`, or null. */
  detail: string | null;
  /** While an attempt's mark outranks an `ok` probe, when that ends. Null otherwise. */
  holdUntil: number | null;
}

export interface MachineCapacityPathView {
  harness: string;
  open: boolean;
  /** Why it is closed, or why it was not judged (`no telemetry`, `no fresh report`, `no floor`). */
  reason: string | null;
  /** Provider model labels (`Fable`, `gpt-5.3-codex-spark`) closed on this path while it is open. */
  closedModels: string[];
}

export interface MachineSidecarView {
  id: number;
  kind: 'footprint' | 'dependency-scan' | 'brief-cleanup' | 'text-task' | 'creation-draft';
  ticketKey: string;
  status: string;
  startedAt: number | null;
}

export interface MachineList {
  generatedAt: number;
  items: MachineListRow[];
}

interface HostInstallerFile {
  filename: string;
  text: string;
}

/** Shown once after Settings mints a runner. The token is not recoverable. */
export interface MintedMachineResponse {
  name: string;
  token: string;
  orchestratorUrl: string;
  installers: {
    toml: HostInstallerFile;
    windows: HostInstallerFile;
    linux: HostInstallerFile;
  };
}

/* ── ops console ──────────────────────────────────────────────────────────── */

/**
 * The wall clock for a stage with no budget of its own. Each attempt is judged against its
 * recorded `timeout_ms` (or the stage default) rather than this number; it stays on the Ops
 * payload for what remains generic.
 */
export { DEFAULT_ATTEMPT_TIMEOUT_MS as OPS_AGENT_TIMEOUT_MS } from './liveness.ts';

/** Failure-type counts look back this many UTC days. Named on the payload. */
export const OPS_FAILURE_WINDOW_DAYS = 7;

/** Look-back windows GET /api/costs/series accepts, in UTC days. */
export const SPEND_SERIES_WINDOWS = [7, 14, 30] as const;
export const SPEND_SERIES_DEFAULT_DAYS = 14;

/**
 * One day's spend for one machine on one project, merged across both ledgers.
 *
 * `machine` is null when the attempt never reached a runner, or when the machine
 * row was deleted and took its attribution with it. `usd` is null when no row in
 * the cell carried a price — never 0, which would read as "this was free" rather
 * than "nobody said". `unpriced` counts the rows in that state, and those rows
 * still contribute their `tokens`.
 */
export interface UiSpendSeriesCell {
  day: string;
  machine: string | null;
  project: string;
  usd: number | null;
  tokens: number;
  entries: number;
  unpriced: number;
}

/**
 * Daily spend broken down by machine and project, for the Ops production chart.
 *
 * `days` is contiguous and oldest-first so an idle day draws as a gap rather than
 * vanishing. `cells` is sparse — only non-empty combinations appear. Totals include
 * sidecar spend, which the Ops spend tiles exclude.
 */
export interface UiSpendSeries {
  generatedAt: number;
  timezone: 'UTC';
  windowDays: number;
  projectSlug: string | null;
  days: string[];
  /** Stable key lists for colour assignment; null machine sorts as the empty string. */
  machines: (string | null)[];
  projects: string[];
  cells: UiSpendSeriesCell[];
  unpricedEntries: number;
}

/** Look-back windows of the review-quality panel, in UTC days ending now. */
export const REVIEW_QUALITY_WINDOWS = [7, 30] as const;

/**
 * AI Review first-pass quality for one project over one window, from `TransitionApplied`.
 *
 * `firstReviewed` counts tickets whose first-ever AI Review decision (`ai_pass` or
 * `ai_findings`) fell in the window; `firstPassed` is those whose first decision was a pass.
 * `landed` counts tickets merged to Done (from Human Review or Merging) in the window, and
 * `bounces` every `ai_findings` those tickets took over their life. The rates are null when
 * their denominator is zero: no data is not a 0% or a 0.0.
 */
export interface UiReviewQualityWindow {
  days: number;
  firstReviewed: number;
  firstPassed: number;
  firstPassRate: number | null;
  landed: number;
  bounces: number;
  bouncesPerLanded: number | null;
}

export interface UiReviewQualityProject {
  projectSlug: string;
  /** One entry per `REVIEW_QUALITY_WINDOWS`, in that order. */
  windows: UiReviewQualityWindow[];
}

/** Payload of GET /api/review-quality. `total` spans every project row listed. */
export interface UiReviewQuality {
  generatedAt: number;
  timezone: 'UTC';
  projectSlug: string | null;
  windows: number[];
  projects: UiReviewQualityProject[];
  total: UiReviewQualityWindow[];
}

export interface UiOpsSpendWindow {
  key: string;
  usd: number | null;
  entries: number;
  unknownEntries: number;
  attempts: number;
  tokensIn: number;
  tokensOut: number;
}

export interface UiOpsQueueState {
  state: TicketState;
  count: number;
}

export interface UiOpsQueueItem {
  attemptId: number;
  ticketKey: string;
  state: TicketState;
  activity: TicketActivity | null;
  ordinal: number;
}

export interface UiOpsAgent {
  attemptId: number;
  ticketKey: string;
  state: TicketState;
  activity: TicketActivity | null;
  machine: string | null;
  startedAt: number;
  elapsedMs: number;
  /** The runner-enforced wall clock this attempt runs to: its recorded budget, else the stage default. */
  timeoutMs: number;
  orphaned: boolean;
}

export interface UiOpsFailureType {
  type: FailureType;
  count: number;
}

/**
 * Acting-console read-model for GET /api/ops.
 *
 * Machines are host-level (not project-scoped). Queue, agents, spend, failures
 * and waiting counts follow `?project=` when it resolves; an unknown slug
 * matches nothing rather than widening.
 */
export interface UiOps {
  generatedAt: number;
  timezone: 'UTC';
  projectSlug: string | null;
  heartbeatStaleMs: number;
  agentTimeoutMs: number;
  machines: {
    live: number;
    stale: number;
    total: number;
    items: MachineListRow[];
  };
  agents: {
    running: number;
    orphans: number;
    items: UiOpsAgent[];
  };
  queue: {
    depth: number;
    byState: UiOpsQueueState[];
    items: UiOpsQueueItem[];
  };
  spend: {
    today: UiOpsSpendWindow;
    week: UiOpsSpendWindow;
    projects: UiOpsSpendWindow[];
  };
  failures: {
    windowDays: number;
    since: number;
    total: number;
    byType: UiOpsFailureType[];
  };
  waiting: {
    human: number;
    attemptLimit: number;
  };
  /**
   * Loops in the recorded dependency graph. Always empty on a healthy database: new edges
   * are refused when they would close one. A row here is data written before that guard
   * existed, and it has to be broken by hand before any ordering question has an answer.
   */
  dependencyCycles: UiOpsDependencyCycle[];
  /**
   * The sidecar lane, reported apart from everything above it.
   *
   * Sidecars do not use worker slots, do not block a ticket and do not appear in the attempt
   * queue, so folding them into the same counters would misreport both: an operator reading
   * "queue depth 40" would go looking for forty tickets that are not moving.
   */
  sidecars: UiOpsSidecars;
}

export interface UiOpsSidecars {
  /** Slots the live machines report, and how many jobs are open against them. */
  capacity: { slots: number; inUse: number };
  queued: number;
  running: number;
  /** Attributed to the day a job was admitted, not the day it spent the money. */
  spentUsdToday: number;
  reservedUsdToday: number;
  projects: UiOpsSidecarProject[];
  /** Open jobs, oldest first. Terminal jobs live in Runs. */
  items: UiOpsSidecarJob[];
}

export interface UiOpsSidecarProject {
  projectId: number;
  projectSlug: string;
  enabled: boolean;
  /** No route means nothing is admitted, whatever the switch says. */
  routed: boolean;
  queued: number;
  running: number;
  spentUsd: number;
  reservedUsd: number;
  capUsd: number | null;
  /** True when the cap has stopped further admission today. */
  capped: boolean;
  /** Current footprints a partial scan did not fully examine. */
  partialCoverage: number;
  /** Scan-made dependency suggestions and sidecar spend, last 7 then last 30 days. */
  precision: UiSuggestionPrecision[];
}

/**
 * What a project's scans suggested in one window, and what became of it.
 *
 * Counted by when a suggestion was first made, with its outcome now. `made` minus the three
 * outcomes is the part retired without a decision (superseded or resolved).
 */
export interface UiSuggestionPrecision {
  days: number;
  made: number;
  /** Became a link: accepted (automatically or by a person), flipped or converted. */
  accepted: number;
  /** A person said no: dismissed, started anyway past it, or undid the automatic link. */
  dismissed: number;
  pending: number;
  footprintUsd: number;
  scanUsd: number;
}

export interface UiOpsSidecarJob {
  id: number;
  kind: 'footprint' | 'dependency-scan' | 'brief-cleanup' | 'text-task' | 'creation-draft';
  status: string;
  ticketKey: string;
  projectSlug: string;
  machineName: string | null;
  model: string | null;
  startedAt: number | null;
  createdAt: number;
}

export interface UiOpsDependencyCycle {
  /** The loop, in order. The last ticket depends on the first. */
  ticketKeys: string[];
}

/* ── reviewers ────────────────────────────────────────────────────────────── */

/** Spawn ids a reviewer model-list entry may name. Unknown names fail closed. */
type ReviewerHarness = 'claude' | 'codex' | 'cursor' | 'kimi';

/**
 * Context the runner materialises for a reviewer.
 *
 * `ticketContext` is always on and cannot be turned off.
 * `mrDiff` defaults on. `fullCheckout` (repo at MR head SHA) and
 * `namedCiArtifacts` default off. Unknown flag names are rejected.
 */
export const REVIEWER_CONTEXT_FLAG_KEYS = [
  'ticketContext',
  'mrDiff',
  'fullCheckout',
  'namedCiArtifacts',
] as const;

export type ReviewerContextFlagKey = (typeof REVIEWER_CONTEXT_FLAG_KEYS)[number];

export interface ReviewerContextFlags {
  ticketContext: true;
  mrDiff: boolean;
  fullCheckout: boolean;
  namedCiArtifacts: boolean;
}

/** Ordered harness+model slot. Same spirit as execution definitions. */
export interface ReviewerModelEntry {
  harness: ReviewerHarness;
  model: string;
  /** Optional reasoning depth for project execution routes. */
  thinking?: import('../config/types.ts').ThinkingLevel | null;
}

/** Contracts name this string; reviewer dispatch matches on it. */
export const CODE_REVIEW_REVIEWER_NAME = 'Code Review';

/**
 * A reviewer is an identity: name, prompt, context flags and vendor exclusion. Which machine,
 * harness and model run it comes from the execution cascade (ticket override, then the
 * project's `execution.reviewer` routes, then the Nightshift default routes), never from the
 * reviewer record, so there is one place to set models rather than two that disagree.
 */
export interface ReviewerRow {
  id: number;
  name: string;
  prompt: string;
  contextFlags: ReviewerContextFlags;
  vendorExclusion: boolean;
  enabled: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface ReviewerList {
  generatedAt: number;
  items: ReviewerRow[];
}

/* ── settings admin ───────────────────────────────────────────────────────── */

/**
 * Public user row. `password_hash` never belongs on this shape.
 */
export interface SettingsUser {
  id: number;
  username: string;
  displayName: string;
  role: 'admin' | 'member';
  disabled: boolean;
  /**
   * GitLab account this user's project visibility comes from.
   *
   * Null on a member means an empty board: nothing to see until an admin links the account.
   * Null on an admin is harmless, because admins are not filtered.
   */
  gitlabUserId: number | null;
  createdAt: number;
}

export interface SettingsUserList {
  users: SettingsUser[];
  /** When cached GitLab membership was last refreshed. Null before the first sync. */
  membershipSyncedAt?: number | null;
}

type AgentTokenPermission = 'read' | 'write';

/** Public agent-token row. The plaintext secret is never on this shape. */
interface AgentTokenProject {
  id: number;
  slug: string;
  name: string;
}

export interface AgentToken {
  id: number;
  userId: number;
  name: string;
  tokenPrefix: string;
  permission: AgentTokenPermission;
  createdAt: number;
  lastUsedAt: number | null;
  revokedAt: number | null;
  expiresAt: number | null;
  allProjects: boolean;
  projects: AgentTokenProject[];
}

export interface AgentTokenList {
  tokens: AgentToken[];
}

export interface MintedAgentTokenResponse extends AgentToken {
  token: string;
}

export interface VerificationProfileSummary {
  id: number;
  name: string;
  ci: 'required' | 'skip' | null;
  contractVersion: number | null;
  buildRequired: boolean | null;
  unitTestsRequired: boolean | null;
  humanReviewRequired: boolean | null;
  reviewerNames: string[];
  captureNames: string[];
  performanceThreshold: { metric: string; comparison: string; value: number } | null;
}

export interface VerificationProfileReviewerOption {
  name: string;
  enabled: boolean;
}

export interface VerificationProfileList {
  generatedAt: number;
  items: VerificationProfileSummary[];
  /** Seeded reviewers the contract editor can name. */
  availableReviewers: VerificationProfileReviewerOption[];
  /** Coded fallback when no ticket override or artefact names a profile. Not a project column. */
  codedDefault: 'standard-code';
  cascadeNote: string;
}

/* ── performance targets ──────────────────────────────────────────────────── */

/**
 * Per-project metric targets the performance-change gate reads.
 * Stored canonical (`fps` → `fps_avg`). Comparisons are lt|lte|gt|gte; `eq` is rejected.
 */
export const PERFORMANCE_TARGET_METRICS = ['load_ms', 'fps_avg', 'fps_p95', 'memory_mb', 'fps'] as const;

export const PERFORMANCE_TARGET_COMPARISONS = ['lt', 'lte', 'gt', 'gte'] as const;

export type PerformanceTargetComparison = (typeof PERFORMANCE_TARGET_COMPARISONS)[number];

export interface PerformanceTargetRow {
  id: number;
  projectId: number;
  metric: string;
  comparison: PerformanceTargetComparison;
  value: number;
  unit: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface PerformanceTargetList {
  generatedAt: number;
  projectId: number;
  items: PerformanceTargetRow[];
}

/**
 * Downloadable contract for Settings → Performance. Schema + example + GitLab
 * job + a prompt an agent can follow to emit `performance.json`.
 */
export interface PerformanceKit {
  schemaVersion: 'v1';
  artifactPaths: string[];
  schemaMarkdown: string;
  exampleJson: string;
  jsonSchema: string;
  gitlabCiYml: string;
  agentPrompt: string;
  projectSlug: string;
  targets: Array<{
    metric: string;
    comparison: string;
    value: number;
    unit: string | null;
  }>;
}

/* ── ticket detail ────────────────────────────────────────────────────────── */

/** Ordered model-list slots the overrides editor can name. */
export const OVERRIDE_ACTIVITY_KEYS = [
  'Refining/Standard',
  'Refining/Research',
  'Refining/Planning',
  'Implementing/Merge or Rebase',
] as const;

export type OverrideActivityKey = (typeof OVERRIDE_ACTIVITY_KEYS)[number];

/**
 * One row of an override: the four-column route the whole product uses. `machine` null
 * means the row is not machine-specific; `thinking` null means the CLI's own default.
 */
export interface TicketOverrideRoute {
  machine: string | null;
  harness: string;
  model: string;
  thinking: string | null;
}

/**
 * One execution-override slot. Null means Auto (cascade to the next scope).
 *
 * `routes` is the canonical ordered list. `harness` and `models` are the shape overrides
 * were saved in before rows carried their own harness; the resolver still reads them, and the
 * editor mirrors the rows into them so older readers (timeline, Auto labels) keep working.
 * `models: []` is rejected at parse time — Auto or a non-empty ordered list.
 */
export interface TicketOverrideSlot {
  machine: string | null;
  harness: string | null;
  models: string[] | null;
  routes?: TicketOverrideRoute[] | null;
}

export interface TicketOverridesView {
  ticket: TicketOverrideSlot;
  states: Partial<Record<TicketState, TicketOverrideSlot>>;
  activities: Partial<Record<OverrideActivityKey, TicketOverrideSlot>>;
  /** Named verification profile. Null means Auto (refiner artefact, then standard-code). */
  verificationProfile: string | null;
  /**
   * Scenarios the captures must demonstrate, as `check:scenario[@viewport][#inputMode]`
   * (`capture:level-3@1280x720#keyboard`). Absent or null means none declared. Pinned into the
   * attempt's contract at dispatch, so "capture passed" cannot hide a scenario nobody ran.
   */
  verificationScenarios?: string[] | null;
  /**
   * The ticket's size tier for cost-aware routing. Null or absent means Auto: the rule in
   * `core/src/pipeline/sized-routing.ts` reads the footprint estimate, the acceptance criteria
   * and the category. `small` lets a stage's first attempt try the light routes.
   */
  sizeTier?: SizeTier | null;
}

/**
 * Size tiers for cost-aware routing. `small` tickets try a stage's light routes first and
 * escalate to the standard routes; `standard` tickets always run the standard routes.
 */
export const SIZE_TIERS = ['small', 'standard'] as const;
export type SizeTier = (typeof SIZE_TIERS)[number];

/** Which list an attempt ran on. */
export type RouteLane = 'light' | 'standard';

/**
 * Why an attempt ran on the list it did, as stored in `attempt_routing.reason`:
 *
 * - `small_ticket` -- light route: small ticket, nothing has gone wrong in this stage yet;
 * - `escalated` -- standard route: a small ticket after a failure, bounce or stall this stage;
 * - `standard_ticket` -- standard route: the ticket is not small;
 * - `ticket_override` -- standard route: the ticket names its own routes for this stage;
 * - `no_light_routes` -- standard route: no light list is configured for the role;
 * - `light_disabled` -- standard route: every light route's vendor is off for the project.
 */
export type RouteLaneReason =
  | 'small_ticket'
  | 'escalated'
  | 'standard_ticket'
  | 'ticket_override'
  | 'no_light_routes'
  | 'light_disabled';

/** What ends a small ticket's light routing for the rest of the stage. */
export type EscalationTrigger = 'failed_attempt' | 'review_bounce' | 'ci_bounce' | 'stall';

/** One attempt's recorded routing choice, as the drawer and timeline show it. */
export interface AttemptRoutingView {
  tier: SizeTier;
  /** `override` when the drawer set the tier, `rule` when the size rule derived it. */
  tierSource: 'override' | 'rule';
  lane: RouteLane;
  reason: RouteLaneReason;
  escalation: EscalationTrigger | null;
  /** The short phrase: "light route (small ticket)", "escalated after review bounce". */
  label: string;
}

/** The ticket's size verdict and its latest routing choice, for the drawer. */
export interface TicketRoutingView {
  tier: SizeTier;
  tierSource: 'override' | 'rule';
  /** Why the rule chose the tier: "2 predicted files in 1 area, 3 criteria". */
  tierDetail: string;
  /** The most recent attempt that recorded a choice, or null before any. */
  latest: (AttemptRoutingView & { attemptId: number; state: TicketState }) | null;
}

/** One execution slot's inherited values, as display strings. */
export interface TicketOverrideAutoSlotLabels {
  machine: string;
  harness: string;
  models: string;
}

/**
 * What Auto will apply from project/system defaults. Shown on override dropdowns.
 *
 * Project routes are per role, so each execution state can inherit something different;
 * `states` carries those and the top-level fields summarise them for the ticket-wide row
 * (one value when every state agrees, otherwise each state named).
 */
export interface TicketOverrideAutoLabels extends TicketOverrideAutoSlotLabels {
  verificationProfile: string;
  /** What Auto decides for the size tier, with the rule's reason: "small (2 files, 3 criteria)". */
  sizeTier: string;
  states: Record<'Refining' | 'Implementing' | 'AI Review', TicketOverrideAutoSlotLabels>;
}

type TicketDetailTimelineKind = 'event' | 'attempt' | 'human' | 'checkpoint';

export interface TicketDetailTimelineEntry {
  id: string;
  kind: TicketDetailTimelineKind;
  at: number;
  title: string;
  detail: string | null;
  eventType: DomainEventType | null;
  attemptId: number | null;
  attemptStatus: AttemptStatus | null;
  action: string | null;
}

export const TICKET_WAITING_ACTIONS = [
  'resume',
  /**
   * Offered only on a park for missing named CI artifacts: one Implementing attempt that
   * produces the captures against the merge request head the ticket already has, without a
   * refinement and without inviting a source change.
   */
  'evidence-repair',
  /**
   * Offered only when `planVerificationRetry` says a retry would do something: a recorded
   * required job, at the awaited head, behind a verification park. Retrying a job reruns its
   * original CI definition, so a job that never existed is refused rather than offered.
   */
  'retry-verification',
  /**
   * Offered on a park when the ticket's current head has failed capture scenarios with an
   * actual image stored and the project declares where baselines live. Publishing is a
   * person's decision; the button opens the comparison, it does not copy anything.
   */
  'approve-baseline',
  'answer',
  'cancel',
  'plan-approve',
  'plan-request-changes',
  'plan-defer',
  'plan-reject',
] as const;

export type TicketWaitingAction = (typeof TICKET_WAITING_ACTIONS)[number];

/**
 * Separates a waiting `reason`'s own sentence from the last attempt's verbatim failure detail.
 *
 * The reason stays one plain string everywhere it is stored or served (MCP `get_ticket` and
 * the board included); this marker only lets the drawer render the detail as the machine wrote
 * it. On 2026-09-12 the breaker parked CELL-44 saying "Fix the route or environment" and
 * nothing else, so the operator changed models six times without ever seeing the push refusal
 * that was the actual cause.
 */
export const WAITING_REASON_DETAIL_MARKER = '\n\nLast failure detail:\n';

/** How much of the last failure detail a waiting reason carries before it is truncated. */
export const WAITING_REASON_DETAIL_MAX_CHARS = 600;

export interface TicketWaitingPanel {
  reasonType: WaitingReasonType;
  /**
   * The breaker's sentence, and -- after {@link WAITING_REASON_DETAIL_MARKER} -- the last
   * failed attempt's `failure_detail` verbatim, when there was one.
   */
  reason: string;
  wakeMode: WakeMode;
  resumeState: TicketState;
  resumeActivity: TicketActivity | null;
  allowedActions: TicketWaitingAction[];
  /** Stored allowed_actions the projection did not recognise — fail closed, shown as text. */
  unknownAllowedActions: string[];
  notify: boolean;
  notifyUserId: number | null;
  notifyUserName: string | null;
  /**
   * Whether a plain Resume would get past the blocker. When it would not, `missing` names
   * the action that does the work, `alternative` is the drawer action for it, and
   * `overridable` says whether the operator may resume anyway.
   */
  resumePrerequisite: { ok: true } | { ok: false; missing: string; alternative: string | null; overridable: boolean };
}

/**
 * The night manager, as the dashboard reads it.
 *
 * One shape serves the compact popover, the expanded activity view and the MCP context the
 * manager itself is given, so the three can never disagree about how far along a run is.
 */
export interface NightManagerCapacityReadingView {
  /** Machine, provider, plan and window. Never an account identifier or an address. */
  label: string;
  thresholdPercent: number;
  remainingPercent: number | null;
  lastKnownPercent: number | null;
  lastKnownAgeMs: number | null;
  breached: boolean;
  /** Set when there is no fresh figure; supervision continues and this is shown as a warning. */
  unavailable: 'no-report' | 'account-changed' | 'window-missing' | 'no-figure' | 'stale' | 'collection-failed' | null;
}

export interface NightManagerActiveTicketView {
  ticketId: number;
  ticketKey: string;
  title: string;
  state: string;
  liveAttempts: number;
  inScope: boolean;
}

export interface NightManagerCountedTicketView {
  ticketId: number;
  ticketKey: string;
  title: string;
  observedAt: number;
  /** True when this ticket was completed outside the manager's own actions. */
  independent: boolean;
  reopenedAt: number | null;
  source: 'transition' | 'reconciliation';
}

interface NightManagerSelectionView {
  satisfied: number[];
  outstanding: Array<{ ticketId: number; ticketKey: string; state: string; waitingOn: number[] }>;
  abandoned: Array<{ ticketId: number; ticketKey: string; state: string }>;
  complete: boolean;
}

export interface NightManagerEventView {
  id: number;
  kind: string;
  actor: 'user' | 'manager' | 'system';
  actorName: string | null;
  createdAt: number;
  payload: Record<string, unknown>;
}

export interface NightManagerRunView {
  id: string;
  projectSlug: string;
  projectName: string;
  status: 'enabled' | 'stopped';
  phase: 'idle' | 'queued' | 'working' | 'recovering' | 'needs-attention';
  /** Why the supervisor is not acting right now, when it is not. */
  waitReason:
    | 'waiting-for-ticket-changes'
    | 'runner-unavailable'
    | 'authentication-required'
    | 'recovering-session'
    | 'blocked-ticket-needs-attention'
    | 'no-capable-route'
    | 'turn-in-flight'
    | null;
  enabledBy: { userId: number; name: string | null };
  policyRevision: number;
  startedAt: number;
  stoppedAt: number | null;
  stopReason: string | null;
  stopReasons: string[];
  summary: string;
  goal: {
    scopeKind: 'selection' | 'backlog';
    selected: Array<{ ticketId: number; ticketKey: string; title: string; state: string }>;
    maxActiveTickets: number;
    checkIntervalMinutes: number;
    instructions: string;
    stop: {
      selectionComplete: boolean;
      ticketTarget: number | null;
      deadline: { at: number; timezone: string; source: 'duration' | 'deadline'; durationMs: number | null; label: string } | null;
      capacity: Array<{
        machineId: number;
        provider: string;
        /**
         * The opaque account digest the condition is pinned to.
         *
         * Carried so an edit round-trips the pin rather than silently re-binding to whichever
         * account is signed in now. It is never rendered: `label` is what the interface shows.
         */
        account: string;
        window: string;
        model: string | null;
        thresholdPercent: number;
        label: string;
      }>;
    };
  };
  routes: Array<{ machine: string | null; harness: string; model: string; thinking: string | null }>;
  progress: {
    completed: number;
    target: number | null;
    counted: NightManagerCountedTicketView[];
    selection: NightManagerSelectionView | null;
  };
  load: { active: number; limit: number; excess: number; tickets: NightManagerActiveTicketView[]; blocked: number };
  capacity: NightManagerCapacityReadingView[];
  lastCheckAt: number | null;
  nextCheckAt: number | null;
  msRemaining: number | null;
  /** Warnings the person should see: stale capacity, no capable route, repeated failures. */
  warnings: string[];
  handoff: NightManagerHandoff | null;
  activity?: { lastActivityAt: number; activity: string; activeTool: string | null } | null;
  timing?: { elapsedMs: number; turnMs: number; waitingMs: number };
  usage: { knownUsd: number; unknownInvocations: number; invocations: number; tokensIn?: number; tokensOut?: number; unreportedTokenInvocations?: number };
}

/** `GET /api/projects/:slug/night-manager` — saved defaults plus the current run. */
export interface NightManagerProjectView {
  projectSlug: string;
  projectName: string;
  /** Whether the reader may enable, edit and stop, as opposed to only watch. */
  canManage: boolean;
  settings: {
    version: number;
    routes: Array<{ machine: string | null; harness: string; model: string; thinking: string | null }>;
    checkIntervalMinutes: number;
    maxActiveTickets: number;
    instructions: string;
  };
  run: NightManagerRunView | null;
  /** Harnesses with a verified manager adapter, and why the others are unavailable. */
  capability: {
    capableHarnesses: string[];
    unavailable: Array<{ harness: string; reason: string }>;
    /** True when no configured route can currently host a manager turn. */
    noCapableRoute: boolean;
  };
  /** Capacity windows offered by the builder, already filtered to fresh, bindable ones. */
  capacityOptions: Array<{
    machineId: number;
    machineName: string;
    provider: string;
    account: string;
    plan: string | null;
    window: string;
    model: string | null;
    remainingPercent: number | null;
    label: string;
  }>;
}

/** Status for every project the reader can see, for the top-bar button's label. */
export interface NightManagerOverview {
  runs: Array<{
    runId: string;
    projectSlug: string;
    projectName: string;
    status: 'enabled' | 'stopped';
    phase: string;
    needsAttention: boolean;
    completed: number;
    target: number | null;
  }>;
  canManageAny: boolean;
}

/** One readiness check, as `assessTicketReadiness` produced it. */
interface TicketReadinessCheckView {
  id: 'acceptance-source' | 'duplicated-requirements' | 'decisions' | 'dependencies' | 'evidence-plan';
  status: 'ok' | 'warn' | 'block';
  detail: string;
}

/**
 * Is the ticket ready to implement, check by check, and what the Brief tab can offer to fix
 * it: a verbatim move of acceptance criteria out of Problem, when that move is unambiguous.
 */
export interface TicketReadinessView {
  ready: boolean;
  checks: TicketReadinessCheckView[];
  acceptancePreview: { problem: string; acceptance: string; moved: string[] } | null;
}

/** One image of a baseline comparison, served by `/api/artifacts/:id/file`. */
interface TicketBaselineImage {
  artifactId: number;
  artifactPath: string;
  sha256: string;
  bytes: number;
}

/** A failed capture scenario whose actual image a person may approve as the new baseline. */
export interface TicketBaselineCandidate {
  scenario: string;
  expectedPath: string;
  message: string | null;
  viewport: string | null;
  jobId: number;
  pipelineId: number;
  headSha: string;
  actual: TicketBaselineImage;
  expected: TicketBaselineImage | null;
  diff: TicketBaselineImage | null;
}

export interface TicketBaselinesView {
  configured: boolean;
  headSha: string | null;
  candidates: TicketBaselineCandidate[];
  /** Whether the approval command would be accepted right now, and why not otherwise. */
  offered: boolean;
  reason: string | null;
}

/** The buckets a ticket's time is sorted into. Concurrent buckets are never summed into one duration. */
export type TicketEffortCategory =
  | 'queue'
  | 'agent'
  | 'ci'
  | 'evidence-transport'
  | 'human-wait'
  | 'automatic-wait'
  | 'landing';

interface TicketEffortCategoryView {
  category: TicketEffortCategory;
  /** Union of the category's intervals: elapsed time a person waited. */
  wallMs: number;
  /** Sum of the category's intervals: resource minutes, which may exceed wall time. */
  resourceMs: number;
  intervals: number;
}

/** One CLI run or sidecar job, with what it cost and whether that is known. */
export interface TicketInvocationCost {
  kind: 'attempt' | 'sidecar';
  id: number;
  label: string;
  status: string;
  startedAt: number | null;
  finishedAt: number | null;
  durationMs: number | null;
  harness: string | null;
  model: string | null;
  tokensIn: number;
  tokensOut: number;
  costUsd: number | null;
  /** `unknown` when no usage was recorded; the total marks it rather than counting zero. */
  source: 'reported' | 'estimated' | 'unknown';
  /** Interrupted, superseded or cancelled: the usage still counts. */
  interrupted: boolean;
}

export interface TicketHoldingView {
  kind: 'done' | 'idle' | 'waiting' | 'human' | 'ci' | 'landing' | 'attempt' | 'queued';
  /** One sentence naming the concrete dependency, job, decision or attempt. */
  what: string;
  since: number;
  ref: { kind: 'attempt' | 'pipeline' | 'waiting' | 'mr'; id: number; label: string } | null;
}

/** Elapsed time, resource time and cost for one ticket, plus what is holding it now. */
export interface TicketEffortView {
  since: number;
  wallMs: number;
  resourceMs: number;
  categories: TicketEffortCategoryView[];
  invocations: TicketInvocationCost[];
  cost: { knownUsd: number; knownCount: number; unknownCount: number };
  /** Repair entries, as annotations: CI repair, review repair, conflict repair, evidence transfer. */
  repairs: Array<{ at: number; category: string; command: string; to: string }>;
  holding: TicketHoldingView;
  /**
   * The latest failure signature and how many times in a row it has come round, with the
   * strategy in force. Null until a repair has recorded a signature.
   */
  recurrence: { summary: string; streak: number; threshold: number; strategy: string; at: number } | null;
  /**
   * The current logical implementation: every Implementing attempt since the ticket last
   * entered Implementing other than by a resume, with the totals across them and whether the
   * latest continuations moved anything. Null until the ticket has implemented.
   */
  continuation: TicketContinuationView | null;
  /** CI queue and execution time from the recorded job timings; null until a pipeline was recorded. */
  ci: { pipelines: number; queuedMs: number; executionMs: number; byRunner: Array<{ runner: string; executionMs: number; jobs: number }> } | null;
}

export interface TicketContinuationView {
  startedAt: number;
  attempts: number;
  continuations: number;
  extensions: number;
  wallMs: number;
  costUsd: number;
  unknownCostCount: number;
  noProgressStreak: number;
  threshold: number;
  /** Newest first. */
  checkpoints: Array<{ attemptId: number; at: number; progressed: boolean; summary: string }>;
}

/** A product decision already made on the ticket. */
export interface TicketDecisionView {
  id: number;
  question: string;
  answer: string;
  actor: string;
  /** The contract revision the decision was made against. */
  contractRevision: number;
  createdAt: number;
}

export interface TicketDetailComment {
  id: number;
  body: string;
  createdAt: number;
  author: string;
  /** False for an agent or system process, which writes without a user account. */
  human: boolean;
}

/**
 * Where one message to a running attempt got to.
 *
 * `queued` means the orchestrator has it and no runner has taken it. `delivered` means the
 * runner that owns the attempt has it. `applied` means it was put in front of the agent --
 * which is what a person means by "did it get there", and the only one of the three that is
 * worth much. `expired` is the honest failure: the attempt ended first.
 */
export type AttemptMessageStatus = 'queued' | 'delivered' | 'applied' | 'expired';

export interface TicketDetailAttemptMessage {
  id: number;
  attemptId: number;
  attemptOrdinal: number | null;
  body: string;
  author: string;
  /** True when the runner wrote it -- a deadline warning, or a note about a repeated gate. */
  fromRunner: boolean;
  createdAt: number;
  deliveredAt: number | null;
  appliedAt: number | null;
  expiredAt: number | null;
  expiredReason: string | null;
  status: AttemptMessageStatus;
}

export interface TicketDetailAttempt {
  phase?: string | null;
  phaseAt?: number | null;
  id: number;
  ordinal: number;
  state: TicketState;
  activity: TicketActivity | null;
  status: AttemptStatus;
  machine: string | null;
  startedAt: number | null;
  finishedAt: number | null;
  mrIid: number | null;
  mrHeadSha: string | null;
  costUsd: number | null;
  /** Why this attempt ran on the route list it did. Absent for attempts from before 0080. */
  routing?: AttemptRoutingView | null;
}

export interface TicketDetailArtifact {
  id: number;
  attemptId: number;
  kind: string;
  path: string | null;
  /**
   * A pointer for most kinds, but a whole markdown document for the document kinds — the
   * planner stores its entire plan here. Over the preview cap this is the leading slice
   * only; `refTruncated` says so and GET /api/artifacts/:id/text has the rest.
   */
  ref: string | null;
  /** Character length of the stored ref before any truncation. Null when there is no ref. */
  refChars: number | null;
  /** True when `ref` is a leading slice, not the whole value. */
  refTruncated: boolean;
  /** True when this kind's ref is prose to render, not a pointer to label. */
  refIsDocument: boolean;
  superseded: boolean;
  createdAt: number;
  stale: boolean;
  staleReason: string | null;
  /**
   * When retention claimed the file at `path`, or null while it is kept. The file is removed a
   * day after this and the row stays; until then GET /api/artifacts/:id/file still serves it.
   */
  fileExpiredAt: number | null;
}

export interface TicketDetailEvidence {
  id: number;
  attemptId: number;
  type: string;
  result: string;
  mrHeadSha: string;
  environment: string | null;
  stale: boolean;
  staleReason: string | null;
  capturedAt: number;
}

/** One finding from one reviewer, as stored in its canonical result. */
interface TicketDetailReviewFinding {
  severity: 'error' | 'warning' | 'info';
  description: string;
  file: string | null;
  line: number | null;
  /** Stable id within the ticket; the same defect reported twice shares it. */
  id: string;
  axis: 'spec' | 'standards' | null;
  cite: string | null;
  /** What earlier rounds say about it, derived from the stored verdicts. */
  state: 'introduced' | 'unresolved' | 'resolved' | 'regressed';
}

/** One acceptance criterion against what a review said about it. */
export interface TicketDetailReviewCriterion {
  /** 1-based; 0 for a criterion the reviewer listed that the ticket does not. */
  index: number;
  criterion: string;
  status: 'met' | 'not-met' | 'unproven' | 'not-checked' | 'not-assessed';
  note: string | null;
  /** Earlier reviewer attempt this row relies on. */
  priorAttemptId: number | null;
}

/** A finding's life across review rounds, derived from the verdicts. */
export interface TicketDetailReviewFindingRecord {
  id: string;
  severity: 'error' | 'warning' | 'info';
  description: string;
  file: string | null;
  line: number | null;
  cite: string | null;
  reviewer: string;
  state: 'introduced' | 'unresolved' | 'resolved' | 'regressed';
  /** Rounds in a row it has been reported, ending at the newest wave; 0 once resolved. */
  streak: number;
  rounds: number;
}

interface TicketDetailReviewComment {
  path: string;
  line: number;
  body: string;
  blocking: boolean;
}

/**
 * One reviewer attempt in the AI Review stage, with its verdict read back from the stored
 * canonical result. `verdict` is null while the reviewer is still running or when the stored
 * result is not a canonical pass/fail (`malformed` says why). Grouped into waves by
 * `mrHeadSha` in the UI; `stale` marks a wave that judged an older head.
 */
export interface TicketDetailReview {
  attemptId: number;
  ordinal: number;
  reviewerName: string | null;
  status: AttemptStatus;
  verdict: 'pass' | 'fail' | null;
  malformed: string | null;
  summary: string;
  approvalRationale: string | null;
  /** Findings at or above the project's `review.findings_shown` level. */
  findings: TicketDetailReviewFinding[];
  /** Lower-severity findings the project setting hides; never blocking ones. */
  hiddenFindings: number;
  flaggedComments: TicketDetailReviewComment[];
  reviewedFiles: string[];
  /** The ticket's criteria against this verdict's `criteria` list; unreported ones say so. */
  criteria: TicketDetailReviewCriterion[];
  coverage: { reported: number; total: number; structuredReported?: boolean };
  specDispute: boolean;
  disputes: Array<{ criterion: string; problem: string }>;
  harness: string | null;
  model: string | null;
  machine: string | null;
  startedAt: number | null;
  finishedAt: number | null;
  durationMs: number | null;
  costUsd: number | null;
  mrHeadSha: string | null;
  stale: boolean;
  staleReason: string | null;
  /** Id of the `ai-review-verdict` artifact when one was written. */
  verdictArtifactId: number | null;
}

/**
 * Read-only answer to "may this merge?" from `GET /api/tickets/:key/merge-readiness`. Runs
 * the same guard the merge action runs, against the live MR, but merges nothing. `checks`
 * is the guard broken into rows a person can read; `ok` is the guard's single answer.
 */
export interface TicketMergeReadiness {
  ok: boolean;
  /** Guard reason when `ok` is false; also set when GitLab could not be read. */
  reason: string | null;
  /** False when the orchestrator has no GitLab configured or the MR could not be read. */
  available: boolean;
  mrIid: number | null;
  mrState: 'open' | 'merged' | 'closed' | null;
  sha: string | null;
  /** GitLab's `detailed_merge_status`, when the MR was read. */
  detailedMergeStatus?: string | null;
  /** True when the live MR head differs from the SHA the ticket's attempts pinned. */
  headMoved: boolean;
  checks: TicketMergeCheck[];
}

export interface TicketMergeCheck {
  /** Stable id: mr-open, conflicts, mergeable, BuildEvidence, TestEvidence, ReviewEvidence, CaptureEvidence, PerformanceEvidence. */
  id: string;
  label: string;
  ok: boolean;
  /** One line a person can act on when `ok` is false; a short confirmation otherwise. */
  note: string;
}

export interface TicketDetailHeader {
  id: number;
  projectId: number;
  projectSlug: string;
  ticketKey: string;
  /** The stored, editable title. Always present, and the only title a save writes. */
  title: string;
  /**
   * The generated title from the current `ticket-display` sidecar result, exactly as the
   * board card shows it. Null when no current result exists, so the drawer and the card
   * fall back to the stored title together rather than disagreeing.
   */
  displayTitle?: string | null;
  displaySummary?: string | null;
  problem: string;
  acceptanceCriteria: string;
  state: TicketState;
  activity: TicketActivity | null;
  milestoneId: number | null;
  milestoneName: string | null;
  version: number;
  elapsedMs: number;
  elapsedFrom: 'updated_at';
  repairs: BoardRepairCount[];
  createdAt: number;
  updatedAt: number;
  machine: string | null;
  harness: string | null;
  model: string | null;
  /** Null until an attempt records an MR. URL is null when no host URL is known. */
  mr: { iid: number; url: string | null } | null;
  agent: string | null;
  /** Why nothing is happening, from the housekeeping detector. Null is the normal case. */
  stalled: string | null;
  /** What the landing loop last decided. The answer to "why is this still in Merging". */
  landing: { outcome: string; detail: string } | null;
}

/**
 * What the drawer prints beside a required check.
 *
 * Five words, and every one of them is derived from a canonical CI fact: `Passed`, `Failed` and
 * `Blocked` come from the stored `verification-result` for the current head, `Running` and
 * `Pending` from the required job's GitLab status while no result exists yet. Nothing here can
 * be produced by an agent upload — CELL-20 uploaded a file named `capture-blocked.txt` and the
 * evidence tab showed a `capture` row, which is the confusion this vocabulary exists to end.
 */
export type VerificationDisplayStatus = 'Passed' | 'Failed' | 'Blocked' | 'Running' | 'Pending';

/** One required check (`launch`, `capture`, …) as the drawer shows it. */
export interface TicketVerificationCheck {
  /** The requirement name from the pinned contract. */
  name: string;
  status: VerificationDisplayStatus;
  /** The pinned producer that owns this requirement, or null when none does. */
  producer: string | null;
  /** The exact CI job name the producer maps to. */
  job: string | null;
  /** The execution the verdict or the wait belongs to. */
  jobId: number | null;
  /** Link to that job in GitLab, when the public origin and repo path are known. */
  jobUrl: string | null;
  /** A bounded sentence saying why this is not `Passed`. Null when it passed. */
  cause: string | null;
}

/**
 * The verification block for a project that has pinned a producer plan.
 *
 * Absent (null) for a legacy project, which keeps showing exactly the capture rows it always
 * has: legacy filename checks must never be dressed up as meeting the new guarantee, and a five-state readout over name-token evidence would do precisely that.
 */
export interface TicketVerificationView {
  /** The head every check below is measured at. Full SHA; the drawer shows eight. */
  headSha: string;
  /** The pinned producer-plan digest a result had to carry to count. */
  digest: string | null;
  checks: TicketVerificationCheck[];
  /** Job retries already spent at this head, operator and automatic together. */
  retries: number;
  /**
   * Whether "Retry verification" can do anything, and the refusal when it cannot.
   *
   * The reason is `planVerificationRetry`'s own wording, so a disabled button says the same
   * thing the API would have said had it been pressed.
   */
  retry: { offered: boolean; reason: string | null };
}

export interface TicketDetail {
  generatedAt: number;
  ticket: TicketDetailHeader;
  waiting: TicketWaitingPanel | null;
  timeline: TicketDetailTimelineEntry[];
  comments: TicketDetailComment[];
  /**
   * Messages sent to attempts while they ran, newest last.
   *
   * Kept apart from `comments` even though a steer writes both: the comment is the record and
   * this is the delivery, and the drawer has to be able to say "sent, never read" about one
   * without saying it about the other.
   */
  attemptMessages: TicketDetailAttemptMessage[];
  attempts: TicketDetailAttempt[];
  artifacts: TicketDetailArtifact[];
  evidence: TicketDetailEvidence[];
  /**
   * Per-check verification status for a project with a pinned producer plan; null for legacy.
   *
   * Sits beside `evidence` rather than replacing it: the aggregate CaptureEvidence rows are
   * still the gate's own record, and this is the readout that says which requirement is
   * stopping the ticket and whether a retry can move it.
   */
  verification: TicketVerificationView | null;
  /** Reviewer attempts with verdicts. Empty until the first AI Review wave starts. */
  reviews: TicketDetailReview[];
  /** Every finding across rounds with its current state. Empty until a verdict is stored. */
  reviewHistory: TicketDetailReviewFindingRecord[];
  overrides: TicketOverridesView;
  /** Set when stored overrides_json is not the current schema. Drawer shows Auto and this note. */
  overridesError: string | null;
  overrideAuto: TicketOverrideAutoLabels;
  /** Size tier and the latest light/standard routing choice. */
  routing: TicketRoutingView;
  deployments: TicketDeployment[];
  deployConfigured: boolean;
  /** True when a merge SHA exists so admin Deploy can play a job. Historic Done is false. */
  deployable: boolean;
  /** Confirmed dependency edges in both directions. */
  dependencies: TicketDependencies;
  /** Readiness checks and the acceptance-move preview. Always present. */
  readiness: TicketReadinessView;
  /** Visual baseline candidates at the current head. Always present; empty for most tickets. */
  baselines: TicketBaselinesView;
  /** Where the ticket's time and money went, and what is holding it now. */
  effort: TicketEffortView;
  /** Product decisions recorded on the ticket, oldest first. */
  decisions: TicketDecisionView[];
  /**
   * Proposals, decision history, footprints and scan status.
   *
   * Always present, even with sidecars switched off: `scanStatus` is then `paused` and says
   * so, which is the honest answer. An absent field would render as "nothing found".
   */
  suggestions: TicketSuggestions;
  /**
   * Set when the landing the ticket is in, or went through, was approved by the project's
   * auto-merge policy rather than a person. Absent or null otherwise.
   */
  policyApproval?: TicketPolicyApprovalView | null;
}

/**
 * One selectable model in the live catalogue served by `GET /api/models/catalogue`.
 *
 * Merged from three sources so a new provider model needs no Nightshift redeploy: the Claude
 * manifest, per-machine runner probes, and the built-in `AGENT_MODELS` floor. `availableOn`
 * lists the machines that reported it; empty means "not machine-specific" (the manifest and
 * the floor), which the editor treats as available everywhere.
 */
export interface UiCatalogueModel {
  id: string;
  label: string;
  harness: Harness;
  /** Thinking levels this model accepts, in the order the source reported them. */
  efforts: string[];
  defaultEffort: string | null;
  /** True when the CLI only accepts the effort baked into the id (`cursor-grok-4.6-high`). */
  effortInId: boolean;
  /** Provider-supplied marker such as `new`; null when the source offers none. */
  badge: string | null;
  status: 'current' | 'legacy' | 'unknown';
  /** Machine names that reported this model. Empty means every machine. */
  availableOn: string[];
  source: 'manifest' | 'machine' | 'builtin';
}

export interface UiCatalogueResponse {
  generatedAt: number;
  models: UiCatalogueModel[];
  /** Per-source freshness and failure notes, for the Settings staleness markers. */
  sources: UiCatalogueSource[];
}

export interface UiCatalogueSource {
  name: string;
  /** Epoch ms of the last successful read, or null when it has never succeeded. */
  fetchedAt: number | null;
  error: string | null;
}

/**
 * One Nightshift-wide default route (Settings → Nightshift → Models). Same four columns as
 * every other route list; `machine` null means the row is not machine-specific.
 */
interface UiDefaultRoute {
  machine: string | null;
  harness: Harness;
  model: string;
  thinking: string | null;
}

/** A utility route: the same four columns, with the sidecar store's `effort` name for thinking. */
interface UiUtilityRoute {
  machine: string | null;
  harness: string;
  model: string;
  effort: string | null;
}

/**
 * GET /api/models/defaults: the two Nightshift-wide lists and the coded fallbacks that apply
 * while a list is empty, so the page can say what "nothing saved" actually runs.
 */
export interface UiModelDefaults {
  /** Bottom of the agent cascade: refine, implement and review when nothing narrower applies. */
  agentRoutes: UiDefaultRoute[];
  /** Cheap background work: footprints, comparisons and card titles unless a project sets its own. */
  utilityRoutes: UiUtilityRoute[];
  /**
   * Nightshift-wide light lists per role (`execution.light_routes`). A small ticket's first
   * attempt of a stage tries its role's list when the standard list came from `agentRoutes`.
   * Empty means no light routing for that role, which is the default.
   */
  lightRoutes: Record<'refiner' | 'implementer' | 'reviewer', UiDefaultRoute[]>;
  fallback: {
    agent: UiDefaultRoute;
    utility: UiUtilityRoute;
  };
}

/**
 * Per route outcomes over a window (Settings → Nightshift → Models), so a light list can be
 * chosen from evidence. One row per stage, harness, model and thinking level that actually
 * ran; the counts are raw so the page can combine stages without averaging averages.
 */
export interface UiModelEvidenceRow {
  stage: 'Refining' | 'Implementing' | 'AI Review';
  harness: string;
  model: string;
  /** Null when neither the route nor the model id named a level. */
  thinking: string | null;
  /** Finished attempts: Succeeded plus Failed. Cancelled, Interrupted and Superseded are not outcomes. */
  attempts: number;
  succeeded: number;
  /** Implementing only: successful implementations that reached an AI Review verdict. */
  reviewed: number;
  /** Of `reviewed`, those whose first review passed. */
  firstPass: number;
  /** Summed priced spend of the successful attempts, and how many of them had a price. */
  successUsd: number;
  successPriced: number;
  /** Summed priced spend of every finished attempt on the route, failures included. */
  spendUsd: number;
}

export interface UiModelEvidence {
  generatedAt: number;
  windowDays: number;
  rows: UiModelEvidenceRow[];
}

/* ── routes overview (Settings → Nightshift → Routes) ─────────────────────── */

/** Who a stored route list belongs to. `nightshift` is the factory-wide layer. */
export type UiRouteScope = 'nightshift' | 'project' | 'ticket';

/** What the live catalogue says about a route's model. */
export type UiRouteModelStatus = 'current' | 'retired' | 'unknown';

/** One row of a route list, in the shared four-column shape. */
export interface UiRouteRow {
  machine: string | null;
  harness: string;
  model: string;
  effort: string | null;
}

export interface UiRouteOwner {
  /** Stable key: `nightshift`, `project:<id>`, `project:<id>:night-manager`, `ticket:<id>`. */
  key: string;
  kind: UiRouteScope;
  label: string;
  projectId: number | null;
  projectSlug: string | null;
  ticketKey: string | null;
}

/** Where the list an entry belongs to is stored. */
export type UiRouteStorage =
  | 'settings'
  | 'project-configuration'
  | 'night-manager'
  | 'night-manager-run'
  | 'ticket-overrides'
  | 'coded-default';

export interface UiRouteEntry {
  /** Unique within one inventory read. */
  id: string;
  /** Registry slot id (`execution.implementer`, `sidecar.utility`, ...). */
  slot: string;
  slotLabel: string;
  /** The stage the list runs for, in words (`Implementing`, `Sidecar`, `Night manager`). */
  stage: string;
  /** Narrower than the stage: a sidecar kind or a ticket override slot. */
  activity: string | null;
  scope: UiRouteScope;
  owner: UiRouteOwner;
  /** Machine section (`execution.machines.<name>.<role>`) the list replaces the general one on. */
  section: string | null;
  /** 1-based position within its list: the order routes are tried in. */
  position: number;
  machine: string | null;
  harness: string;
  model: string;
  effort: string | null;
  /** True when this owner stores the list; false when it inherits it. */
  explicit: boolean;
  /** For an inherited entry: the list it comes from. */
  inheritedFrom: string | null;
  storage: UiRouteStorage;
  catalogue: UiRouteModelStatus;
  /** Which catalogue source knows the model, when one does. */
  catalogueSource: 'manifest' | 'machine' | 'builtin' | null;
  /** False when the harness or model is switched off where this entry applies. */
  enabled: boolean;
  /** Why the entry may not run as written: disabled, not reported by its machine, and so on. */
  problems: string[];
  /** Whether a bulk change can write this entry (inherited and live-run entries cannot). */
  editable: boolean;
}

/** One harness + model across the inventory, for the summary counts. */
export interface UiRouteModelUsage {
  harness: string;
  model: string;
  catalogue: UiRouteModelStatus;
  routes: number;
  explicit: number;
  projects: number;
  tickets: number;
  efforts: Array<string | null>;
}

interface UiRouteSlotInfo {
  id: string;
  label: string;
  stage: string;
  scope: UiRouteScope;
  resettable: boolean;
}

export interface UiRouteProjectInfo {
  id: number;
  slug: string;
  name: string;
}

export interface UiRouteAuditEntry {
  id: number;
  changeId: string;
  ownerKey: string;
  ownerLabel: string;
  action: 'replace' | 'reset';
  summary: string;
  actor: string | null;
  createdAt: number;
}

/** GET /api/routes: every stored or inherited route in the product. */
export interface UiRouteInventory {
  generatedAt: number;
  entries: UiRouteEntry[];
  models: UiRouteModelUsage[];
  slots: UiRouteSlotInfo[];
  projects: UiRouteProjectInfo[];
  recentChanges: UiRouteAuditEntry[];
  catalogueSources: UiCatalogueSource[];
}

/** Which owners and lists a bulk change may touch. */
export interface UiRouteSelection {
  /** Project ids. Absent or empty means every project. */
  projects?: number[];
  /** Include the Nightshift-wide lists. Defaults to true. */
  nightshift?: boolean;
  /** Registry slot ids. Absent or empty means every slot. */
  slots?: string[];
  /** Also rewrite per-ticket overrides on open tickets. Defaults to false. */
  tickets?: boolean;
}

export interface UiRouteMatch {
  harness: string;
  model: string;
  /** Absent means any thinking level; null means routes that name none. */
  effort?: string | null;
}

export interface UiRouteTarget {
  harness: string;
  model: string;
  /** Absent keeps each route's own level where the new model offers it. */
  effort?: string | null;
}

export type UiRouteChange =
  | { kind: 'replace'; from: UiRouteMatch; to: UiRouteTarget; selection: UiRouteSelection }
  | { kind: 'reset'; selection: UiRouteSelection };

/** One list's before and after within an owner. An empty list means "inherits". */
export interface UiRoutePlanList {
  slot: string;
  slotLabel: string;
  stage: string;
  activity: string | null;
  section: string | null;
  before: UiRouteRow[];
  after: UiRouteRow[];
}

export interface UiRoutePlanOwner {
  owner: UiRouteOwner;
  storage: UiRouteStorage;
  /** Opaque fingerprint of the stored state the plan was computed from. */
  guard: string;
  lists: UiRoutePlanList[];
  /** Set when this owner cannot take the change; it is left alone and the rest still apply. */
  blocked: string | null;
  warnings: string[];
}

/** POST /api/routes/plan: the diff a change would make, before anything is written. */
export interface UiRoutePlan {
  change: UiRouteChange;
  owners: UiRoutePlanOwner[];
  /** Owners that will be written. */
  changed: number;
  blocked: number;
  /** Route rows that differ across every owner that will be written. */
  routesChanged: number;
  /** Notes about the whole change, such as live night-manager runs it does not touch. */
  notes: string[];
  /** Fingerprint of every written owner's guard and result; apply refuses a different one. */
  token: string;
}

/** POST /api/routes/apply result. */
export type UiRouteApplyResult =
  | { ok: true; changeId: string; changed: number; plan: UiRoutePlan }
  | { ok: false; conflict: true; message: string; plan: UiRoutePlan }
  | { ok: false; conflict?: false; message: string };

/* ── project graph read model (NS-1) ──────────────────────────────────────── */

/**
 * Whether the projection describes a real graph, a project the caller cannot have, or a
 * read that fell over.
 *
 * Three values rather than two because an empty project and a failed read look identical
 * once they are flattened into "no nodes", and they lead a person to do different things.
 * `unknown-project` is deliberately the same answer for a slug that does not exist and one
 * the session may not see: the caller must not be able to tell those apart.
 */
type GraphStatus = 'ok' | 'unknown-project' | 'failed';

export type GraphCategory = WorkCategory;

/**
 * Where a node's effective category came from.
 *
 * Only `none` occurs today: neither estimated categories nor human overrides are persisted
 * yet (NS-6). The other two exist so that adding those sources is a change of value, not a
 * change of contract, and so no consumer has to infer a category from anything else.
 */
type GraphCategorySource = 'override' | 'estimate' | 'none';

/** Why the effective category is what it is, in the caller's language. */
type GraphCategoryProvenance =
  | 'human-override'
  | 'current-estimate'
  | 'stale-estimate'
  | 'legacy-estimate'
  | 'not-estimated'
  | 'invalid-value';

export interface GraphNodeCategory {
  /** The one effective primary category. `unclassified` until a real source supplies one. */
  category: GraphCategory;
  source: GraphCategorySource;
  provenance: GraphCategoryProvenance;
  /** The estimator's short rationale once one is stored. Null while no source exists. */
  reason: string | null;
}

/**
 * How current the stored footprint estimate is for this ticket.
 *
 * `unknown` is not "missing": it means an estimate exists but its stored brief identity
 * cannot be compared with the live ticket, so claiming either currency or staleness would
 * be a guess.
 */
export type GraphEstimateState = 'missing' | 'current' | 'stale' | 'unknown';

/** How much dependency comparison this ticket's current estimate has actually had. */
export type GraphScanState = 'not-scanned' | 'complete' | 'partial' | 'stale' | 'unknown';

interface GraphNodeFreshness {
  estimate: GraphEstimateState;
  scan: GraphScanState;
  /** When the current estimate was recorded; null when there is none. */
  estimatedAt: number | null;
}

/**
 * One card's worth of ticket. Deliberately compact: briefs, footprints, evidence and
 * decision history stay in the existing detail endpoints.
 */
export interface GraphNode {
  id: number;
  ticketKey: string;
  title: string;
  /**
   * The ticket's contract revision, so a canvas category edit can send `expectedVersion` and
   * be told about a concurrent contract change instead of silently overwriting one.
   */
  version: number;
  /** Bounded plain-text line from the first meaningful prose paragraph. May be empty. */
  excerpt: string;
  /** Bounded card content from the contract and canonical CI/deployment observations. */
  cardContent?: {
    displayTitle?: string | null;
    microDescription?: string | null;
    description: string;
    acceptanceCriteria: string[];
    ci: BoardCiStatus | null;
    shipping: BoardShippingStatus | null;
    deployment: Pick<TicketDeployment, 'status' | 'healthStatus' | 'releaseVersion' | 'environment'> | null;
  };
  /** The ticket's actual lifecycle state — never a board column. */
  state: TicketState;
  milestoneId: number | null;
  category: GraphNodeCategory;
  freshness: GraphNodeFreshness;
  updatedAt: number;
}

export interface GraphMilestone {
  id: number;
  name: string;
  /** Tickets in this milestone within this projection. Zero is a real, kept value. */
  ticketCount: number;
  /** Milestones this one directly comes after and waits for. */
  after: number[];
  /** Milestones this one is placed after without waiting for them. */
  orderAfter: number[];
  /**
   * Column in the milestone order, both kinds: 0 comes after nothing. Regions are laid out by rank.
   * When the project's phases are linked, phase order counts too, so the ticket graph and the
   * milestone graph put each milestone in the same column.
   */
  rank: number;
  /** The phase this milestone belongs to, by name and registry id, or null. */
  phase: string | null;
  phaseId: number | null;
  /** The horizontal band its phase runs along when phases are linked; null when they are not. */
  lane: number | null;
}

/**
 * A confirmed `depends_on` edge, named by role rather than by storage direction.
 *
 * `ticket_links.from_ticket_id` is the dependent and `to_ticket_id` is the prerequisite.
 * Mapping database `from` to canvas `source` would reverse every arrow on the page, so the
 * roles are named here once and carried unchanged through layout.
 */
export interface GraphDependency {
  id: number;
  prerequisiteId: number;
  dependentId: number;
  /** The canonical current dependency chip status derived from the prerequisite. */
  status: DependencyChipState;
  createdAt: number;
  /** Suggestions that resolve to this same pair, so provenance survives deduplication. */
  suggestionIds: number[];
}

export interface GraphSuggestion {
  id: number;
  /** Optimistic concurrency token for a decision made from this projection. */
  inputRevision: number;
  prerequisiteId: number;
  dependentId: number;
  band: 'high' | 'medium';
  reason: string;
  evidence: string[];
  status: 'proposed' | 'decided' | 'superseded' | 'resolved';
  decision: string | null;
  createdFrom: 'scan' | 'overlap-rule' | 'flip' | 'convert';
  /** True while still `proposed`: only these may inform the current arrangement. */
  current: boolean;
  /** True when the inputs this was derived from are no longer either ticket's current ones. */
  staleInput: boolean;
  /** Set when a confirmed dependency already expresses this pair. */
  confirmedDependencyId: number | null;
  updatedAt: number;
}

/**
 * A shared-file overlap: an unordered pair with a type, never an implementation order.
 *
 * `ticketIds` is stored lowest id first (the schema enforces it), so the same overlap can
 * never appear twice under two orderings.
 */
export interface GraphOverlap {
  id: number;
  /** Optimistic concurrency token for a decision made from this projection. */
  inputRevision: number;
  kind: 'shared-files';
  ticketIds: [number, number];
  band: 'high' | 'medium';
  reason: string;
  evidence: string[];
  status: 'proposed' | 'decided' | 'superseded' | 'resolved';
  decision: string | null;
  current: boolean;
  staleInput: boolean;
  updatedAt: number;
}

type GraphCoverageCaveatId =
  | 'footprint-identity'
  | 'sidecars-paused'
  | 'classification-unavailable';

/** A named limitation of the data above, in a sentence a person can read. */
export interface GraphCoverageCaveat {
  id: GraphCoverageCaveatId;
  detail: string;
}

export interface GraphCoverageSummary {
  tickets: number;
  currentEstimates: number;
  staleEstimates: number;
  missingEstimates: number;
  unknownEstimates: number;
  scanComplete: number;
  scanPartial: number;
  scanStale: number;
  notScanned: number;
  scanUnknown: number;
  caveats: GraphCoverageCaveat[];
}

interface GraphProjectIdentity {
  id: number;
  slug: string;
  name: string;
}

/**
 * The whole authorised project, on demand.
 *
 * `revision` is the store revision the response was built at, so a consumer can tell a
 * refresh apart from a stale repeat. `project` is null unless `status` is `ok`.
 */
export interface GraphProjection {
  status: GraphStatus;
  revision: number;
  generatedAt: number;
  project: GraphProjectIdentity | null;
  milestones: GraphMilestone[];
  /** Every registered phase, with the phases it is planned after (order only). */
  phases: Array<{ id: number; name: string; orderAfter: number[] }>;
  nodes: GraphNode[];
  dependencies: GraphDependency[];
  suggestions: GraphSuggestion[];
  overlaps: GraphOverlap[];
  coverage: GraphCoverageSummary;
  /** Set only when `status` is `failed`. Never a silent empty graph. */
  error: string | null;
}

/**
 * One line to draw. Dependencies and overlaps stay distinguishable by `kind`, and an
 * accepted suggestion that matches a confirmed link collapses into a single `dependency`
 * relationship that still names the suggestion it came from.
 */
export type GraphRelationshipKind = 'dependency' | 'suggested-dependency' | 'overlap';

export interface GraphRelationship {
  id: string;
  kind: GraphRelationshipKind;
  /** Prerequisite for the directed kinds; the lower ticket id for an overlap. */
  sourceId: number;
  /** Dependent for the directed kinds; the higher ticket id for an overlap. */
  targetId: number;
  /** False for overlaps: an unordered pair never acquires an arrowhead. */
  directed: boolean;
  confirmedDependencyId: number | null;
  suggestionIds: number[];
  band: 'high' | 'medium' | null;
  staleInput: boolean;
}

export type { AssistedRunReport } from './assisted-run-report.ts';
export type { HarnessCliMachineView, HarnessCliOverview, HarnessCliRowView } from '../harness-cli/view-types.ts';

/** A bundled prompt, optional persisted override, or read-only generated example. */
export interface UiPromptTemplate {
  id: string;
  name: string;
  group: string;
  source: string;
  description: string;
  editable: boolean;
  text: string;
  defaultText: string;
  overridden: boolean;
  revision: string;
  updatedAt: number | null;
  updatedBy: string | null;
}
export interface SharedGraphArrangement {
  placement?: 'straight' | 'balanced';
  positions: Record<string, { x: number; y: number; parent?: string | null; width?: number; height?: number; resized?: boolean }>;
}

export interface SharedGraphLayout {
  revision: number;
  arrangement: SharedGraphArrangement;
}

/** How the policy described the landing it approved, for the drawer's banner. */
export interface TicketPolicyApprovalView {
  at: number;
  sha: string;
  policy: string;
  reasons: string[];
}

type UiReviewSensitivePathKind =
  | 'pipeline'
  | 'ci'
  | 'runner-script'
  | 'lockfile'
  | 'migration'
  | 'credentials';

export interface UiReviewQueueReviewer {
  name: string;
  verdict: 'pass' | 'fail' | 'missing';
  errors: number;
  warnings: number;
}

/**
 * One ticket waiting for a person in Human Review, with what a reviewer needs to decide how
 * closely to look: how big the change is, whether it touches anything sensitive, how often it
 * bounced and what the AI reviewers said. `null` means not recorded, never zero.
 */
export interface UiReviewQueueItem {
  ticketId: number;
  key: string;
  title: string;
  projectSlug: string;
  projectName: string;
  /** Sent back with Approve so a ticket that moved since the list loaded is refused, not approved. */
  version: number;
  /** When the ticket entered Human Review. */
  since: number;
  mrIid: number | null;
  mrUrl: string | null;
  headSha: string | null;
  changedLines: number | null;
  added: number | null;
  removed: number | null;
  filesChanged: number | null;
  binary: boolean;
  sensitivePaths: { path: string; kind: UiReviewSensitivePathKind }[] | null;
  bounces: number;
  reviewWaves: number;
  reviewers: UiReviewQueueReviewer[];
  category: WorkCategory;
  /**
   * The project's auto-merge policy: whether it is on, and when it declined this ticket, why.
   * `declined` is null when the policy is off or said nothing about this head.
   */
  autoMerge: { enabled: boolean; declined: string[] | null };
}

export interface UiReviewQueue {
  generatedAt: number;
  items: UiReviewQueueItem[];
}

export type { LibraryAction } from '../runner-libraries.ts';
export type { WorktreeAssessment, WorktreeRetentionMode } from '../runner-storage.ts';
/** Database-backed Help page, keyed by the stable documentation slug. */
export interface UiWikiPage {
  id: string;
  title: string;
  markdown: string;
  navigationOrder: number;
  updatedAt: number;
}
